import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Diamond, Sparkles, MessageCircle, BookOpen, BarChart2, Check, Link as LinkIcon, FileText, Upload, Crown, Store, BrainCircuit, Search, Plus, Menu, MessageSquare, TrendingUp, Trash2, ChevronDown, ChevronLeft, ChevronRight, Loader2, Layers, Zap, User, Layout, Calendar, History, Building2, Eye, Wand2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useAuthFetch } from './utils/authFetch';
import { optimizeImageUrl } from './utils/imageUtils';

import ReactMarkdown from 'react-markdown';
import MotifDisplay from './MotifDisplay';
import VisualSearch from './VisualSearch';
import './App.css';

// Inline so it survives CSP img-src (which allows data: but no placeholder host)
const NO_IMAGE_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Crect width='60' height='60' fill='%23efe9e4'/%3E%3C/svg%3E";

const MicIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor"><path d="M480-400q-50 0-85-35t-35-85v-240q0-50 35-85t85-35q50 0 85 35t35 85v240q0 50-35 85t-85 35Zm0-240Zm-40 520v-123q-104-14-172-93T200-520h80q0 83 58.5 141.5T480-320q83 0 141.5-58.5T680-520h80q0 106-68 184t-172 93v123h-80Zm40-360q17 0 28.5-11.5T520-520v-240q0-17-11.5-28.5T480-800q-17 0-28.5 11.5T440-760v240q0 17 11.5 28.5T480-480Z" /></svg>
);

const StopIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor"><path d="M320-320h320v-320H320v320ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" /></svg>
);

// --- Skeleton Loader for product cards ---
const SkeletonProductCard = () => (
  <div className="product-card skeleton-card">
    <div className="skeleton-image" />
    <div className="skeleton-body">
      <div className="skeleton-line skeleton-title" />
      <div className="skeleton-line skeleton-price" />
      <div className="skeleton-specs">
        <div className="skeleton-line skeleton-spec" />
        <div className="skeleton-line skeleton-spec" />
        <div className="skeleton-line skeleton-spec" />
        <div className="skeleton-line skeleton-spec" />
      </div>
      <div className="skeleton-line skeleton-footer" />
    </div>
  </div>
);


// Premium Analysis Dossier Component
const JewelleryAnalysisDossier = ({ result }) => {
  const [activeDossierTab, setActiveDossierTab] = useState('identity');

  if (!result) return null;
  
  let rawData;
  try {
    const cleanResult = result.trim().replace(/^```json\n/, '').replace(/\n```$/, '');
    rawData = JSON.parse(cleanResult);
  } catch (e) {
    const jsonMatch = result.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        rawData = JSON.parse(jsonMatch[0]);
      } catch (e2) {
        return null;
      }
    } else {
      return null;
    }
  }

  if (!rawData) return null;

  // Helper to check if a value is meaningful (not empty, null, or "Absent"/"N/A")
  const hasValue = (val) => {
    if (val === null || val === undefined) return false;
    if (typeof val === 'string') {
      const v = val.trim().toLowerCase();
      return v !== '' && v !== 'absent' && v !== 'n/a' && v !== 'none' && v !== 'no' && v !== '0' && v !== 'undefined';
    }
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'object') return Object.keys(val).length > 0;
    if (typeof val === 'number') return val > 0;
    return !!val;
  };

  // Normalized Data Mapping
  const data = {
    identification: rawData.jewellery_identification || rawData.identification || {},
    design: rawData.design_language_analysis || rawData.design_language || {},
    motifs: rawData.motif_and_symbolism_analysis || rawData.motifs || {},
    craft: rawData.indian_craftsmanship_cues || rawData.craftsmanship || {},
    materials: rawData.stone_and_embellishment_analysis || rawData.materials || {},
    finish: rawData.metal_and_finish_analysis || {},
    styling: rawData.indian_styling_outfit_pairing || rawData.styling || {},
    story: rawData.final_luxury_catalogue_description || rawData.storytelling || {},
    scale: rawData.scale_and_wearability_perception || {},
    occasions: rawData.occasion_mapping_ratings_out_of_10 || {},
    profile: rawData.target_customer_profile || {},
    recreation: rawData.ai_recreation_preservation_notes || {},
    movement: rawData.movement_profile || {},
    body: rawData.body_suitability || {},
    colors: rawData.color_system || {}
  };

  // Helper to ensure we are working with an array (handles strings, nulls, etc.)
  const toArr = (val) => {
    if (!hasValue(val)) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(Boolean);
    return [val];
  };

  const Section = ({ title, children, icon, condition = true, className = '' }) => {
    if (!condition) return null;
    return (
      <div className={`dossier-section ${className}`}>
        <div className="dossier-section-header">
          {icon && <span className="dossier-icon">{icon}</span>}
          <h3>{title}</h3>
        </div>
        <div className="dossier-section-content">
          {children}
        </div>
      </div>
    );
  };

  const Badge = ({ label, value, type = 'default' }) => {
    if (!hasValue(value)) return null;
    return (
      <div className={`dossier-badge ${type}`}>
        <span className="badge-label">{label}</span>
        <span className="badge-value">{value}</span>
      </div>
    );
  };

  const category = data.identification.indian_category_name || data.identification.category || 'Jewellery';
  const vibe = data.story.premium_ecommerce_sentence || data.story.vibe || 'Exquisite Piece';

  const tabs = [
    { id: 'identity', label: 'Identity', icon: <Crown size={16} /> },
    { id: 'materials', label: 'Materials', icon: <Diamond size={16} /> },
    { id: 'motifs', label: 'Motifs', icon: <Sparkles size={16} /> },
    { id: 'styling', label: 'Styling', icon: <User size={16} /> }
  ];

  return (
    <div className="jewellery-analysis-dossier">
      <div className="dossier-hero">
        <div className="dossier-title-group">
          <span className="dossier-subtitle">{category}</span>
          <h2 className="dossier-title">{vibe}</h2>
        </div>
        <div className="dossier-meta-grid">
          <Badge label="Style" value={data.design.ethnic_styling_direction || data.design.style_influence} type="primary" />
          <Badge label="Era" value={data.design.design_era_reference || data.design.era} />
          <Badge label="Richness" value={data.design.ornamental_richness_level || data.design.richness_level} />
          <Badge label="Scale" value={data.scale.scale} />
        </div>
      </div>

      {/* Luxury Tabs Navigation */}
      <div className="dossier-tabs-nav">
        {tabs.map(t => (
          <button
            key={t.id}
            className={`dossier-tab-btn ${activeDossierTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveDossierTab(t.id)}
          >
            <span className="tab-icon">{t.icon}</span>
            <span className="tab-label">{t.label}</span>
          </button>
        ))}
      </div>

      <div className="dossier-grid">
        {activeDossierTab === 'identity' && (
          <>
            {/* Curator's Note styled as Drop-cap Editorial block */}
            {hasValue(data.story?.emotional_indian_luxury_sentence || data.story?.emotional_narrative) && (
              <div className="dossier-quote-container">
                <p className="dossier-editorial-note">
                  {data.story?.emotional_indian_luxury_sentence || data.story?.emotional_narrative}
                </p>
              </div>
            )}
            
            {/* Design Identity */}
            <Section 
              title="Design Identity" 
              icon={<Crown size={18} />} 
              className="full-width"
              condition={hasValue(data.design.overall_visual_identity || data.design.visual_identity)}
            >
              <p className="dossier-narrative">{data.design.overall_visual_identity || data.design.visual_identity}</p>
              {(hasValue(data.identification.traditional_name_variations) || hasValue(data.identification.traditional_names)) && (
                <div className="traditional-names">
                  <strong>Traditional Names:</strong> {(data.identification.traditional_name_variations || data.identification.traditional_names).join(', ')}
                </div>
              )}
              {hasValue(data.design.heritage_cues) && (
                <div className="heritage-cues">
                  <strong>Heritage Cues:</strong> {data.design.heritage_cues}
                </div>
              )}
            </Section>

            {/* Regional & Cultural Heritage */}
            <Section 
              title="Regional Heritage" 
              icon={<History size={18} />} 
              condition={hasValue(rawData.regional_influence_analysis)}
            >
              <div className="heritage-list">
                {rawData.regional_influence_analysis?.map((influence, i) => (
                  <div key={i} className="heritage-item">
                    <Sparkles size={12} style={{ color: 'var(--primary-gold)' }} />
                    <span>{influence}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* Visual Dominance Analysis */}
            <Section 
              title="Visual Hierarchy" 
              icon={<Eye size={18} />} 
              condition={hasValue(rawData.visual_dominance_analysis?.surface_split_percentages) || hasValue(rawData.visual_dominance_analysis?.first_read_dominance)}
            >
              <div className="dominance-container">
                {hasValue(rawData.visual_dominance_analysis?.first_read_dominance) && (
                  <div className="first-read">
                    <strong>First Impression:</strong> {rawData.visual_dominance_analysis?.first_read_dominance}
                  </div>
                )}
                {hasValue(rawData.visual_dominance_analysis?.surface_split_percentages) && (
                  <div className="split-grid">
                    {Object.entries(rawData.visual_dominance_analysis?.surface_split_percentages || {}).map(([key, val]) => (
                      hasValue(val) && (
                        <div key={key} className="split-item">
                          <span className="split-label">{key.replace(/_/g, ' ')}</span>
                          <span className="split-value">{val}</span>
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>
            </Section>
          </>
        )}

        {activeDossierTab === 'materials' && (
          <>
            {/* Materials */}
            <Section 
              title="Material Excellence" 
              icon={<Diamond size={18} />} 
              condition={hasValue(data.finish.metal_type_and_karat || data.materials.metal_type) || hasValue(data.materials.stone_inventory || data.materials.stones)}
            >
              <div className="material-summary">
                {hasValue(data.finish.metal_type_and_karat || data.materials.metal_type) && (
                  <div className="metal-info">
                    <strong>{data.finish.metal_type_and_karat || data.materials.metal_type}</strong> 
                    {hasValue(data.finish.metal_tone || data.materials.metal_tone) && <> in <span>{data.finish.metal_tone || data.materials.metal_tone}</span></>}
                    {hasValue(data.finish.polish_level || data.materials.finish) && <> ({data.finish.polish_level || data.materials.finish})</>}
                  </div>
                )}
                {hasValue(data.materials.stone_inventory || data.materials.stones) && (
                  <div className="stone-inventory">
                    {toArr(data.materials.stone_inventory || data.materials.stones).map((stone, idx) => (
                      <div key={idx} className="stone-item">
                        <div className="stone-main">
                          <span className="stone-name">{stone.name_indian_hindi || stone.name_in || stone.name_english || stone.name_en}</span>
                          {hasValue(stone.name_indian_hindi || stone.name_in) && hasValue(stone.name_english || stone.name_en) && (
                            <span className="stone-eng">({stone.name_english || stone.name_en})</span>
                          )}
                        </div>
                        <div className="stone-specs">
                          {[stone.cut_style || stone.cut, stone.color_plain_language || stone.color, stone.setting_style || stone.setting]
                            .filter(hasValue).join(' • ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Section>

            {/* Meenakari Deep Dive */}
            {hasValue(rawData.meenakari_surface_technique_deep_dive) && rawData.meenakari_surface_technique_deep_dive.meenakari_present === 'Yes' && (
              <Section 
                title="Surface Techniques" 
                icon={<Layers size={18} />} 
              >
                <div className="meena-summary">
                  <div className="meena-header">
                    <strong>Meenakari Work</strong>
                    <span className="meena-school">{rawData.meenakari_surface_technique_deep_dive?.school_inference}</span>
                  </div>
                  <div className="meena-colors">
                    {rawData.meenakari_surface_technique_deep_dive?.colors?.map((c, i) => (
                      <span key={i} className="meena-color-dot" style={{ backgroundColor: c?.toLowerCase() }}></span>
                    ))}
                  </div>
                  <p className="dossier-details">{rawData.meenakari_surface_technique_deep_dive?.technique_appearance}</p>
                </div>
              </Section>
            )}

            {/* Craftsmanship */}
            <Section 
              title="Master Craftsmanship" 
              icon={<Wand2 size={18} />} 
              condition={hasValue(data.craft.visible_indications) || hasValue(data.craft.techniques)}
            >
              <div className="craft-tags">
                {toArr(data.craft.visible_indications || data.craft.techniques).map((tech, idx) => (
                  <span key={idx} className="craft-tag">{tech}</span>
                ))}
              </div>
              {hasValue(data.craft.details) && <p className="dossier-details">{data.craft.details}</p>}
              {hasValue(data.craft.handcrafted_intricacy_level) && (
                 <div className="artisan-level">Intricacy: {data.craft.handcrafted_intricacy_level}</div>
              )}
              {hasValue(data.craft.artisan_detailing_level) && (
                 <div className="artisan-level">Finish: {data.craft.artisan_detailing_level}</div>
              )}
            </Section>

            {/* Structural Breakdown */}
            <Section 
              title="Structural Analysis" 
              icon={<Layout size={18} />} 
              condition={hasValue(rawData.structural_breakdown)}
            >
              <div className="structural-grid">
                <Badge label="Silhouette" value={rawData.structural_breakdown?.main_silhouette} />
                <Badge label="Symmetry" value={rawData.structural_breakdown?.symmetry} />
                <Badge label="Density" value={rawData.structural_breakdown?.openwork_vs_dense} />
                <Badge label="Curvature" value={rawData.structural_breakdown?.curvature} />
              </div>
              {hasValue(rawData.structural_breakdown?.negative_space_usage) && (
                <div className="structural-detail">
                  <strong>Spatial Usage:</strong> {rawData.structural_breakdown?.negative_space_usage}
                </div>
              )}
            </Section>
          </>
        )}

        {activeDossierTab === 'motifs' && (
          <>
            {/* Motifs */}
            <Section 
              title="Motif & Symbolism" 
              icon={<Sparkles size={18} />} 
              className="full-width"
              condition={hasValue(data.motifs.floral_motifs) || hasValue(data.motifs.botanical_nature_motifs) || hasValue(data.motifs.fauna_motifs) || hasValue(data.motifs.celestial_cosmic_motifs) || hasValue(data.motifs.geometric_motifs) || hasValue(data.motifs.heritage_temple_deity_motifs) || hasValue(data.motifs.motif_details) || hasValue(data.motifs.floral) || hasValue(data.motifs.botanical) || hasValue(data.motifs.fauna) || hasValue(data.motifs.celestial) || hasValue(data.motifs.geometric) || hasValue(data.motifs.heritage) || hasValue(data.motifs.structural)}
            >
              <div className="motif-categories">
                {Object.entries(data.motifs).map(([cat, items]) => {
                  if (cat === 'motif_details' || !hasValue(items)) return null;
                  const itemList = toArr(items);
                  if (itemList.length === 0) return null;
                  return (
                    <div key={cat} className="motif-group">
                      <span className="motif-category-label">{cat.replace(/_/g, ' ')}</span>
                      <div className="motif-chips">
                        {itemList.map((item, idx) => <span key={idx} className="motif-chip">{item}</span>)}
                      </div>
                    </div>
                  );
                })}
              </div>
              {hasValue(data.motifs.motif_details) && (
                <div className="motif-deep-dive">
                  {toArr(data.motifs.motif_details).map((m, idx) => (
                    <div key={idx} className="motif-detail-item">
                      <strong>{m.motif_name}:</strong> {m.symbolic_cultural_association} <span className="prominence-tag">{m.prominence}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Color Palette */}
            <Section 
              title="Color Palette" 
              icon={<Zap size={18} />} 
              className="full-width"
              condition={hasValue(data.colors.dominant_color_impression) || hasValue(data.colors.overall_color_temperature) || hasValue(data.colors.stone_colors)}
            >
              <div className="color-system">
                 <div className="color-header">
                   <span className="color-impression">{data.colors.dominant_color_impression}</span>
                   <span className="color-temp">{data.colors.overall_color_temperature}</span>
                 </div>
                 {hasValue(data.colors.stone_colors) && (
                   <div className="color-strip">
                     {toArr(data.colors.stone_colors).map((c, i) => (
                       <span key={i} className="color-pill" style={{ borderColor: 'var(--primary-gold)' }}>{c}</span>
                     ))}
                   </div>
                 )}
                 {hasValue(data.colors.auspicious_combination) && (
                   <div className="auspicious-note">
                     ✨ {data.colors.auspicious_combination}
                   </div>
                 )}
              </div>
            </Section>
          </>
        )}

        {activeDossierTab === 'styling' && (
          <>
            {/* Perception & Movement */}
            <Section 
              title="Perception & Movement" 
              icon={<BarChart2 size={18} />} 
              className="full-width"
              condition={hasValue(data.scale.weight_appearance) || hasValue(data.scale.daily_wear_practicality) || hasValue(data.movement.static_fluid_swing)}
            >
              <div className="scale-grid">
                <Badge label="Apparent Weight" value={data.scale.weight_appearance} />
                <Badge label="Daily Wear" value={data.scale.daily_wear_practicality} />
                <Badge label="Comfort" value={data.scale.comfort_perception} />
                <Badge label="Suitability" value={data.scale.layering_suitability} />
              </div>
              {hasValue(data.movement.static_fluid_swing) && (
                <div className="movement-profile">
                   <strong>Movement:</strong> {data.movement.static_fluid_swing}
                </div>
              )}
            </Section>

            {/* Body Suitability */}
            <Section 
              title="Styling & Fit" 
              icon={<User size={18} />} 
              condition={hasValue(data.body.skin_tones) || hasValue(data.body.face_shapes)}
            >
              <div className="body-suitability">
                {hasValue(data.body.skin_tones) && (
                  <div className="suitability-item">
                    <span className="suitability-label">Skin Tones</span>
                    <div className="suitability-tags">
                      {toArr(data.body.skin_tones).map((t, i) => <span key={i} className="suitability-tag">{t}</span>)}
                    </div>
                  </div>
                )}
                {hasValue(data.body.face_shapes) && (
                  <div className="suitability-item">
                    <span className="suitability-label">Face Shapes</span>
                    <div className="suitability-tags">
                      {toArr(data.body.face_shapes).map((t, i) => <span key={i} className="suitability-tag">{t}</span>)}
                    </div>
                  </div>
                )}
              </div>
            </Section>

            {/* Styling & Pairing */}
            <Section 
              title="Styling & Pairing" 
              icon={<Store size={18} />} 
              className="full-width"
              condition={hasValue(data.styling.festive_styling) || hasValue(data.styling.outfit_pairings) || hasValue(data.styling.sarees) || hasValue(data.styling.lehengas)}
            >
              <div className="styling-grid">
                 {hasValue(data.styling.festive_styling) && (
                   <div className="styling-item">
                     <span className="styling-label">Festive</span>
                     <div className="styling-values">{toArr(data.styling.festive_styling).map((v, i) => <span key={i}>{v}</span>)}</div>
                   </div>
                 )}
                 {(hasValue(data.styling.outfit_pairings) || hasValue(data.styling.sarees) || hasValue(data.styling.lehengas)) && (
                   <div className="styling-item">
                     <span className="styling-label">Outfits</span>
                     <div className="styling-values">
                       {[...toArr(data.styling.outfit_pairings), ...toArr(data.styling.sarees), ...toArr(data.styling.lehengas)]
                         .filter((v, i, self) => self.indexOf(v) === i)
                         .map((v, i) => <span key={i}>{v}</span>)}
                     </div>
                   </div>
                 )}
              </div>
            </Section>

            {/* Occasion Mapping */}
            <Section 
              title="Occasion Mapping" 
              icon={<Calendar size={18} />} 
              condition={hasValue(data.occasions) && Object.values(data.occasions).some(v => v > 0)}
            >
              <div className="occasion-ratings">
                {Object.entries(data.occasions).map(([occ, val]) => {
                  if (!hasValue(val) || val === 0) return null;
                  return (
                    <div key={occ} className="rating-row">
                      <span className="rating-label">{occ.replace(/_/g, ' ')}</span>
                      <div className="rating-bar-container">
                        <div className="rating-bar" style={{ width: `${(val / 10) * 100}%` }}></div>
                      </div>
                      <span className="rating-value">{val}/10</span>
                    </div>
                  );
                })}
              </div>
            </Section>

            {/* Customer Profile */}
            <Section 
              title="Customer Profile" 
              icon="🎯" 
              condition={hasValue(data.profile)}
            >
              <div className="profile-grid">
                <Badge label="Age Range" value={data.profile?.likely_age_range} />
                <Badge label="Style" value={data.profile?.traditional_vs_modern} />
                <Badge label="Segment" value={data.profile?.boutique_vs_mass_market} />
                <Badge label="Persona" value={data.profile?.trend_follower_vs_heritage_lover} />
              </div>
            </Section>
          </>
        )}
      </div>
    </div>
  );
};

const ProductCard = ({ product, onImageClick, onCardClick }) => {
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  // Parse images securely
  let images = ['https://via.placeholder.com/200?text=No+Image'];
  if (product.image_urls) {
    const splitImages = product.image_urls.split(', ');
    if (splitImages.length > 0) {
      images = splitImages;
    }
  }

  const handleNextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImgIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImgIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Helper to extract clean specs
  const getPurity = () => {
    if (!product.metals) return null;
    const match = product.metals.match(/Purity \(Karatage\):\s*([^|]+)/);
    return match ? match[1].trim() : null;
  };

  const getWeight = () => {
    if (!product.gold_weight) return null;
    return product.gold_weight.replace('Weight:', '').trim();
  };

  const getStoneInfo = () => {
    // Check both diamond_details and specs for stone/diamond weight
    const combined = `${product.diamond_details || ''} | ${product.product_specifications || ''}`;
    const bits = combined.split('|');
    const diamondWeight = bits.find(b => b.includes('Diamond Weight'))?.split(':')[1]?.trim();
    const stoneWeight = bits.find(b => b.includes('Stone Weight'))?.split(':')[1]?.trim();
    const stoneCount = bits.find(b => b.includes('Stone Count'))?.split(':')[1]?.trim();

    let info = [];
    if (diamondWeight) info.push(<span key="d" className="stone-stat-badge"><Diamond size={12} className="inline-icon" /> {diamondWeight}</span>);
    if (stoneWeight) info.push(<span key="s" className="stone-stat-badge"><Sparkles size={12} className="inline-icon" /> {stoneWeight}</span>);
    if (stoneCount) info.push(<span key="c" className="stone-stat-count">({stoneCount} pcs)</span>);

    return info.length > 0 ? <div className="stone-stats-container">{info}</div> : null;
  };

  const getGoldColor = () => {
    const combined = `${product.metals || ''} | ${product.diamond_details || ''} | ${product.product_specifications || ''}`;
    const match = combined.match(/Metal Color:\s*([^|]+)/);
    return match ? match[1].trim() : null;
  };

  const getDiamondType = () => {
    if (!product.diamond_details) return null;
    const match = product.diamond_details.match(/Diamond Type:\s*([^|]+)/);
    return match ? match[1].trim() : null;
  };

  const getStoneType = () => {
    const combined = `${product.diamond_details || ''} | ${product.product_specifications || ''}`;
    const match = combined.match(/Stone Type:\s*([^|]+)/);
    return match ? match[1].trim() : null;
  };

  const getGemstoneType = () => {
    const combined = `${product.diamond_details || ''} | ${product.product_specifications || ''}`;
    const match = combined.match(/Gemstone Type:\s*([^|]+)/);
    return match ? match[1].trim() : null;
  };

  const getGemstoneWeight = () => {
    const combined = `${product.diamond_details || ''} | ${product.product_specifications || ''}`;
    const match = combined.match(/Gemstone Weight:\s*([^|]+)/);
    return match ? match[1].trim() : null;
  };

  const getDiamondClarity = () => {
    const combined = `${product.diamond_details || ''} | ${product.product_specifications || ''}`;
    const match = combined.match(/Diamond Clarity:\s*([^|]+)/);
    return match ? match[1].trim() : null;
  };

  const getDiamondWeight = () => {
    const combined = `${product.diamond_details || ''} | ${product.product_specifications || ''}`;
    const match = combined.match(/Diamond Weight:\s*([^|]+)/);
    return match ? match[1].trim() : null;
  };

  const getDiamondColor = () => {
    const combined = `${product.diamond_details || ''} | ${product.product_specifications || ''}`;
    const match = combined.match(/Diamond Color:\s*([^|]+)/);
    return match ? match[1].trim() : null;
  };

  const purity = getPurity();
  const weight = getWeight();
  const goldColor = getGoldColor();
  const diamondType = getDiamondType();
  const stoneType = getStoneType();
  const stoneInfo = getStoneInfo();
  const diamondClarity = getDiamondClarity();
  const diamondColor = getDiamondColor();
  const diamondWeight = getDiamondWeight();
  const gemstoneType = getGemstoneType();
  const gemstoneWeight = getGemstoneWeight();

  return (
    <div className="product-card" onClick={(e) => {
      if (onCardClick) onCardClick(product);
      window.open(product.product_url, '_blank');
    }} style={{ cursor: 'pointer' }}>
      <div className="card-image-section">
        {product.category && (
          <span className="category-badge">{product.category}</span>
        )}

        <div className="image-gallery-wrapper">
          <img
            src={optimizeImageUrl(images[currentImgIndex])}
            alt={product.name}
            loading="lazy"
            onClick={(e) => {
              if (onImageClick) {
                e.stopPropagation();
                onImageClick(product);
              }
            }}
            onKeyDown={(e) => {
              if (onImageClick && (e.key === 'Enter' || e.key === ' ')) {
                e.stopPropagation();
                e.preventDefault();
                onImageClick(product);
              }
            }}
            tabIndex={onImageClick ? 0 : -1}
            role={onImageClick ? "button" : undefined}
            aria-label={onImageClick ? `Zoom image for ${product.name}` : undefined}
            style={onImageClick ? { cursor: 'zoom-in' } : {}}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/200?text=No+Image';
            }}
          />

          {images.length > 1 && (
            <>
              <button className="gallery-btn prev-btn" onClick={handlePrevImage} aria-label="Previous image">‹</button>
              <button className="gallery-btn next-btn" onClick={handleNextImage} aria-label="Next image">›</button>
              <div className="gallery-dots">
                {images.map((_, i) => (
                  <span key={i} className={`dot ${i === currentImgIndex ? 'active' : ''}`} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="product-details">
        <div className="details-header">
          <h4 title={product.name}>{product.name}</h4>
          {product.sku && <p className="sku" style={{fontSize: 'var(--text-xs)', color: 'var(--text-light)', margin: '0 0 4px 0'}}>SKU: {product.sku}</p>}
          <p className="price">₹{Number(product.price).toLocaleString()}</p>
        </div>

        <div className="product-specs-grid">
          {purity && (
            <div className="spec-item">
              <span className="spec-label">Purity</span>
              <span className="spec-value">{purity}</span>
            </div>
          )}
          {goldColor && (
            <div className="spec-item">
              <span className="spec-label">Metal Color</span>
              <span className="spec-value">{goldColor}</span>
            </div>
          )}
          {weight && (
            <div className="spec-item">
              <span className="spec-label">Metal Weight</span>
              <span className="spec-value">{weight}</span>
            </div>
          )}
          {diamondClarity && (
            <div className="spec-item">
              <span className="spec-label">Diamond Clarity</span>
              <span className="spec-value">{diamondClarity}</span>
            </div>
          )}
          {diamondColor && (
            <div className="spec-item">
              <span className="spec-label">Diamond Color</span>
              <span className="spec-value">{diamondColor}</span>
            </div>
          )}
          {diamondWeight && (
            <div className="spec-item">
              <span className="spec-label">Diamond Carats</span>
              <span className="spec-value">{diamondWeight}</span>
            </div>
          )}
          {diamondType && (
            <div className="spec-item">
              <span className="spec-label">Diamond Type</span>
              <span className="spec-value">{diamondType}</span>
            </div>
          )}
          {gemstoneType && (
            <div className="spec-item">
              <span className="spec-label">Gemstone Type</span>
              <span className="spec-value">{gemstoneType}</span>
            </div>
          )}
          {gemstoneWeight && (
            <div className="spec-item">
              <span className="spec-label">Gemstone Weight</span>
              <span className="spec-value">{gemstoneWeight}</span>
            </div>
          )}
          {stoneType && (
            <div className="spec-item">
              <span className="spec-label">Stone Type</span>
              <span className="spec-value">{stoneType}</span>
            </div>
          )}
          {stoneInfo && (
            <div className="spec-item full-width">
              <span className="spec-label">Stone Stats</span>
              <div className="spec-value">{stoneInfo}</div>
            </div>
          )}
          {product.sku && (
            <div className="spec-item sku-item">
              <span className="spec-label">SKU</span>
              <span className="spec-value">{product.sku}</span>
            </div>
          )}
        </div>

        <div className="card-footer">
          <span className="availability-tag">{product.availability || 'In Stock'}</span>
          <button className="view-link-btn">View Details</button>
        </div>
      </div>
    </div>
  );
};

const CustomModal = ({ isOpen, title, message, onConfirm, onCancel, confirmText = "OK", cancelText }) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen) {
      if (!dialog.open) {
        dialog.showModal();
      }
    } else {
      if (dialog.open) {
        dialog.close();
      }
    }
  }, [isOpen]);

  const handleClose = () => {
    if (onCancel) {
      onCancel();
    } else if (onConfirm) {
      onConfirm();
    }
  };

  return (
    <dialog 
      ref={dialogRef} 
      onClose={handleClose} 
      closedby="any" 
      className="custom-native-dialog"
    >
      <div className="modal-content">
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="modal-actions">
          {cancelText && <button onClick={onCancel} className="modal-cancel-btn">{cancelText}</button>}
          <button onClick={onConfirm} className="modal-confirm-btn">{confirmText}</button>
        </div>
      </div>
    </dialog>
  );
};


const ImageLightbox = ({ isOpen, imageUrl, onClose }) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen) {
      if (!dialog.open) {
        dialog.showModal();
      }
    } else {
      if (dialog.open) {
        dialog.close();
      }
    }
  }, [isOpen]);

  const handleClose = () => {
    if (onClose) onClose();
  };

  // Close when backdrop is clicked
  const handleBackdropClick = (e) => {
    if (e.target === dialogRef.current) {
      handleClose();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      onClose={handleClose}
      onClick={handleBackdropClick}
      className="custom-lightbox-dialog"
    >
      <div className="lightbox-content">
        <button className="lightbox-close-btn" onClick={handleClose}>×</button>
        {imageUrl && <img src={optimizeImageUrl(imageUrl, 1200)} alt="Zoomed Product View" className="lightbox-image" />}
      </div>
    </dialog>
  );
};


function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, login, logout, setUser } = useAuth();
  const authFetch = useAuthFetch();

  const role = auth.role;
  const selectedUser = auth.selectedUser;

  const [adminTokenInput, setAdminTokenInput] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [usersList, setUsersList] = useState([]);


  useEffect(() => {
    fetch('/api/users')
      .then(res => res.json())
      .then(data => setUsersList(data))
      .catch(console.error);
  }, []);

  const [activeTab, setActiveTab] = useState('chat');
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);

  // Sync URL to activeTab
  useEffect(() => {
    const path = location.pathname;
    if (path === '/login') return; // Handled by role

    if (path.startsWith('/chat')) {
      setActiveTab('chat');
      const parts = path.split('/');
      if (parts[2]) setCurrentSessionId(parts[2]);
      else setCurrentSessionId(null);
    } else if (path === '/analysis') {
      setActiveTab('analysis');
    } else if (path === '/visual') {
      setActiveTab('visual');
    } else if (path === '/knowledge') {
      setActiveTab('knowledge');
    } else if (path === '/dashboard') {
      setActiveTab('dashboard');
    } else {
      if (role && path !== '/login') navigate('/chat');
    }
  }, [location.pathname, role, navigate]);

  const handleTabChange = (tab) => {
    if (tab === 'chat' && currentSessionId) {
      navigate('/chat/' + currentSessionId);
    } else {
      navigate('/' + tab);
    }
  };

  const fetchSessions = useCallback(async () => {
    if (role && selectedUser) {
      try {
        const res = await fetch(`/api/sessions?user_id=${selectedUser.id}`);
        const data = await res.json();
        if (Array.isArray(data)) setSessions(data);
      } catch (e) { console.error(e); }
    }
  }, [role, selectedUser]);

  useEffect(() => {
    if (role) fetchSessions();
  }, [role, fetchSessions]);

  useEffect(() => {
    const loadSession = async () => {
      if (!currentSessionId) {
        setMessages([{ role: 'bot', text: 'Welcome to Indriya AI. How can I assist you with the jewellery catalogue today?', isWelcome: true }]);
        return;
      }
      try {
        const res = await fetch(`/api/sessions/${currentSessionId}/messages?userId=${selectedUser.id}`);
        const msgs = await res.json();
        if (Array.isArray(msgs) && msgs.length > 0) {
          const formatted = msgs.map(m => ({
            role: m.sender === 'ai' ? 'bot' : 'user',
            text: m.text,
            products: m.products ? (typeof m.products === 'string' ? JSON.parse(m.products) : m.products) : []
          }));
          setMessages(formatted);
        } else {
          setMessages([{ role: 'bot', text: 'Welcome to Indriya AI. How can I assist you with the jewellery catalogue today?', isWelcome: true }]);
        }
      } catch (e) { console.error(e); }
    };
    if (role && activeTab === 'chat' && selectedUser?.id) loadSession();
  }, [currentSessionId, role, activeTab, selectedUser?.id]);

  const handleNewChat = () => {
    setCurrentSessionId(null);
    navigate('/chat');
  };

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'en-IN');
  const chatLogRef = useRef(null);
  const refinementCardRef = useRef(null);




  // Analysis Tab State
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(() => {
    const saved = localStorage.getItem('jewel_selected_product');
    return saved ? parseInt(saved) : null;
  });

  // Sync selected product to localStorage so it stays open on refresh
  useEffect(() => {
    if (selectedProductId) {
      localStorage.setItem('jewel_selected_product', selectedProductId);
    } else {
      localStorage.removeItem('jewel_selected_product');
    }
  }, [selectedProductId]);

  const [analysisResult, setAnalysisResult] = useState('');
  const [analyzingProductIds, setAnalyzingProductIds] = useState(new Set());
  const [isBulkAnalyzing, setIsBulkAnalyzing] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isRefineOpen, setIsRefineOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    setIsRefineOpen(false);
  }, [selectedProductId]);

  // Click outside and Escape key handler for Refine AI Copilot drawer
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isRefineOpen && refinementCardRef.current && !refinementCardRef.current.contains(event.target)) {
        setIsRefineOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (isRefineOpen && event.key === 'Escape') {
        setIsRefineOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isRefineOpen]);

  const [refinementFeedback, setRefinementFeedback] = useState('');
  const [bulkAnalysisProgress, setBulkAnalysisProgress] = useState({ current: 0, total: 0 });
  const [totalUnanalyzedCount, setTotalUnanalyzedCount] = useState(0);
  const [selectedForBulk, setSelectedForBulk] = useState(new Set());
  const [productPage, setProductPage] = useState(1);

  // Derive the active analysis text dynamically
  useEffect(() => {
    if (selectedProductId) {
      if (analyzingProductIds.has(selectedProductId)) {
        setAnalysisResult('Analyzing product... please wait.');
      } else {
        const product = products.find(p => p.id === selectedProductId);
        if (product && product.ai_description) {
          setAnalysisResult(product.ai_description);
        } else {
          setAnalysisResult('');
        }
      }
    }
  }, [selectedProductId, analyzingProductIds, products]);

  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [analysisSearch, setAnalysisSearch] = useState('');
  const [analysisSort, setAnalysisSort] = useState('newest');
  const [isAnalysisSortOpen, setIsAnalysisSortOpen] = useState(false);
  const [analysisImageIndex, setAnalysisImageIndex] = useState(0);
  const [activeAnalysisSubTab, setActiveAnalysisSubTab] = useState('gallery'); // 'gallery' or 'report'
  const [isImageCollapsed, setIsImageCollapsed] = useState(false);


  // Knowledge Base State
  const [kbFile, setKbFile] = useState(null);
  const [kbLink, setKbLink] = useState('');
  const [isUploadingKb, setIsUploadingKb] = useState(false);
  const [kbUploadResult, setKbUploadResult] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [kbSources, setKbSources] = useState([]);
  const [selectedKbPreview, setSelectedKbPreview] = useState(null);
  const [isDownloadingPreview, setIsDownloadingPreview] = useState(false);

  // Modal State
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    confirmText: 'OK',
    cancelText: null
  });

  const showAlert = useCallback((title, message) => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      onConfirm: () => setModalConfig(prev => ({ ...prev, isOpen: false })),
      onCancel: null,
      confirmText: 'OK',
      cancelText: null
    });
  }, []);

  const showConfirm = useCallback((title, message, onConfirmCallback) => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        setModalConfig(prev => ({ ...prev, isOpen: false }));
        if (onConfirmCallback) onConfirmCallback();
      },
      onCancel: () => setModalConfig(prev => ({ ...prev, isOpen: false })),
      confirmText: 'Yes, Delete',
      cancelText: 'Cancel'
    });
  }, []);

  // Admin Dashboard State
  const [adminMetrics, setAdminMetrics] = useState({ topProducts: [], storePerformance: [], recentSearches: [], categoryTrends: [], dailyVolume: [], inactiveStoreCount: 0, adminActivity: null });

  const fetchAdminMetrics = useCallback(async () => {
    try {
      const res = await authFetch('/api/admin/metrics');
      const data = await res.json();
      if (res.ok) {
        setAdminMetrics(prev => ({ ...prev, ...data }));
      }
    } catch (e) {
      console.error('Failed to fetch admin metrics:', e);
    }
  }, [authFetch]);

  const [isKbLoading, setIsKbLoading] = useState(false);
  const [kbError, setKbError] = useState(null);

  const fetchKbSources = useCallback(async () => {
    setIsKbLoading(true);
    setKbError(null);
    try {
      const res = await authFetch('/api/knowledge-sources');
      const data = await res.json();
      if (res.ok) {
        setKbSources(data.sources || []);
      } else {
        setKbError(data.error || 'Unauthorized or failed to fetch sources.');
      }
    } catch (e) {
      console.error('Failed to fetch KB sources:', e);
      setKbError('Connection failed while fetching inventory.');
    } finally {
      setIsKbLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (activeTab === 'knowledge') {
      fetchKbSources();
    } else if (activeTab === 'dashboard') {
      fetchAdminMetrics();
    }
  }, [activeTab, fetchKbSources, fetchAdminMetrics]);

  const handleKbPreview = async (sourceName) => {
    if (selectedKbPreview?.sourceName === sourceName) {
      setSelectedKbPreview(null);
      return;
    }
    setIsDownloadingPreview(true);
    try {
      const res = await authFetch(`/api/knowledge-source-preview?sourceName=${encodeURIComponent(sourceName)}`);
      const data = await res.json();
      if (res.ok) {
        setSelectedKbPreview({
          sourceName,
          chunks: data.chunks,
          fullContent: data.fullContent,
          viewMode: 'full'
        });
      } else {
        showAlert('Error', data.error || 'Failed to fetch preview');
      }
    } catch (e) {
      console.error('Failed to fetch KB preview:', e);
      showAlert('Error', 'Connection failed while fetching preview');
    } finally {
      setIsDownloadingPreview(false);
    }
  };

  const handleKbDelete = async (sourceName) => {
    showConfirm('Delete Knowledge Source', `Are you sure you want to delete "${sourceName}"?`, async () => {
      try {
        const res = await authFetch('/api/knowledge-sources', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ sourceName }),
        });
        if (res.ok) {
          fetchKbSources();
        } else {
          showAlert('Error', 'Failed to delete source');
        }
      } catch (e) {
        showAlert('Error', 'Error deleting source');
      }
    });
  };

  const handleKbUpload = async () => {
    if (!kbFile && !kbLink.trim()) return;
    setIsUploadingKb(true);
    setKbUploadResult('');

    const formData = new FormData();
    if (kbFile) formData.append('file', kbFile);
    if (kbLink.trim()) formData.append('link', kbLink.trim());

    try {
      const res = await authFetch('/api/upload-knowledge', {
        method: 'POST',
        body: formData,
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const text = decoder.decode(value);
        const lines = text.split('\n\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              if (data.status === 'complete') {
                setKbUploadResult(data.message);
                setKbFile(null);
                setKbLink('');
                fetchKbSources();
              } else if (data.status === 'error') {
                setKbUploadResult(`Error: ${data.error}`);
              } else {
                setKbUploadResult(data.message);
              }
            } catch (e) { }
          }
        }
      }
    } catch (e) {
      setKbUploadResult('Upload failed: ' + e.message);
    } finally {
      setIsUploadingKb(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'analysis') {
      const handler = setTimeout(() => {
        setProductPage(1);
        setHasMoreProducts(true);
        fetchProducts(1, analysisSort, analysisSearch);
        fetchTotalUnanalyzedCount();
      }, analysisSearch ? 500 : 0); // Debounce if searching

      return () => clearTimeout(handler);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, analysisSort, analysisSearch]);

  const fetchTotalUnanalyzedCount = async () => {
    try {
      const res = await fetch('/api/products/unanalyzed-count');
      const data = await res.json();
      setTotalUnanalyzedCount(data.count || 0);
    } catch (e) {
      console.error('Failed to fetch unanalyzed count:', e);
    }
  };

  const stripStructuredData = (text) => {
    if (!text) return '';
    // Strip everything from "MOTIF & SYMBOLISM ANALYSIS" or "STRUCTURED DATA" onwards
    const pattern = /(?:MOTIF & SYMBOLISM ANALYSIS|MASTER SEARCH INDEX|SEARCH ALIAS BANK|STRUCTURED DATA|32\.|25\.|27\.)[\s\S]*$/i;
    let cleanText = text.replace(pattern, '').trim();
    
    // Also strip JSON blocks if they exist
    cleanText = cleanText.replace(/\{[\s\S]*\}/g, '').trim();
    cleanText = cleanText.replace(/```json[\s\S]*```/g, '').trim();
    cleanText = cleanText.replace(/```[\s\S]*```/g, '').trim();
    
    // Preserve formatting but fix some issues
    cleanText = cleanText.replace(/\(([^)]+)\)\(([^)]+)\)/g, '$1, $2'); // Fix (flower)(blossom) -> flower, blossom
    cleanText = cleanText.replace(/\n{3,}/g, '\n\n'); // Limit consecutive newlines
    
    return cleanText;
  };

  const handleProductSelect = (productId) => {
    setSelectedProductId(productId);
    setAnalysisImageIndex(0);
  };

  const fetchProducts = async (page = 1, sort = analysisSort, search = analysisSearch) => {
    if (isLoadingProducts) return;
    setIsLoadingProducts(true);
    try {
      const res = await fetch(`/api/products?page=${page}&limit=50&sortBy=${sort}&search=${encodeURIComponent(search)}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        if (data.length < 50) setHasMoreProducts(false);
        setProducts(prev => {
          if (page === 1) return data;
          const newItems = data.filter(d => !prev.some(p => p.id === d.id));
          return [...prev, ...newItems];
        });
      } else {
        console.error('API did not return an array for products:', data);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const loadMoreProducts = () => {
    if (!isLoadingProducts && hasMoreProducts) {
      const nextPage = productPage + 1;
      setProductPage(nextPage);
      fetchProducts(nextPage, analysisSort, analysisSearch);
    }
  };

  const handleAnalyze = async () => {
    const currentId = selectedProductId;
    if (!currentId || analyzingProductIds.has(currentId)) return;

    setAnalyzingProductIds(prev => {
      const next = new Set(prev);
      next.add(currentId);
      return next;
    });

    try {
      const res = await authFetch('/api/analyze-product', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: currentId })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Server responded with ${res.status}`);
      }

      const data = await res.json();
      const analysisText = data.analysis || 'Analysis failed: No description generated.';

      // AUTO-SAVE to Database
      const saveRes = await authFetch('/api/save-ai-description', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ productId: currentId, aiDescription: analysisText })
      });

      if (!saveRes.ok) {
        const saveErrorData = await saveRes.json().catch(() => ({}));
        throw new Error(saveErrorData.error || 'Failed to auto-save the generated description to the catalogue.');
      }

      const structured = data.structured;

      // Update local product list state to reflect analyzed status
      setProducts(prev => prev.map(p => p.id === currentId ? { 
        ...p, 
        ai_description: analysisText, 
        structured_analysis: structured,
        motif_details: structured?.motif_details || null 
      } : p));
      setAnalysisResult(analysisText);
      showAlert('Success', 'Heritage Report generated and saved successfully.');

    } catch (err) {
      console.error('Analysis error:', err);
      showAlert('Error', 'Failed during analysis: ' + err.message);
    } finally {
      setAnalyzingProductIds(prev => {
        const next = new Set(prev);
        next.delete(currentId);
        return next;
      });
    }
  };

  const handleBulkAnalyze = async () => {
    if (selectedForBulk.size === 0) {
      showAlert('Info', 'Please select products to analyze first.');
      return;
    }

    const productsToAnalyze = products.filter(p => selectedForBulk.has(p.id) && !p.ai_description);

    if (productsToAnalyze.length === 0) {
      showAlert('Info', 'All selected products are already analyzed.');
      return;
    }

    // Add all selected products to the analyzing set immediately
    setAnalyzingProductIds(prev => {
      const next = new Set(prev);
      productsToAnalyze.forEach(p => next.add(p.id));
      return next;
    });

    setIsBulkAnalyzing(true);
    setBulkAnalysisProgress({ current: 0, total: productsToAnalyze.length });

    let successfulSaves = 0;
    let failures = 0;

    for (let i = 0; i < productsToAnalyze.length; i++) {
      const product = productsToAnalyze[i];
      setBulkAnalysisProgress(prev => ({ ...prev, current: i + 1 }));

      try {
        const res = await authFetch('/api/analyze-product', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productId: product.id })
        });

        if (res.ok) {
          const data = await res.json();
          const analysisText = data.analysis;

          // AUTO-SAVE each one
          const saveRes = await authFetch('/api/save-ai-description', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ productId: product.id, aiDescription: analysisText })
          });

          if (saveRes.ok) {
            successfulSaves++;
            // Update local product list so it reflects analyzed status instantly
            setProducts(prev => prev.map(p => p.id === product.id ? { ...p, ai_description: analysisText } : p));
          } else {
            failures++;
            console.error(`Failed to save product ${product.id}: HTTP ${saveRes.status}`);
          }
        } else {
          failures++;
          console.error(`Failed to analyze product ${product.id}: HTTP ${res.status}`);
        }
      } catch (err) {
        failures++;
        console.error(`Failed to analyze product ${product.id}:`, err);
      } finally {
        // Remove from analyzing set as soon as this specific item is done
        setAnalyzingProductIds(prev => {
          const next = new Set(prev);
          next.delete(product.id);
          return next;
        });
      }

      // Small delay between requests to be gentle on the API
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setIsBulkAnalyzing(false);

    // Clear only the ones we just successfully processed from the bulk selection
    setSelectedForBulk(prev => {
      const next = new Set(prev);
      productsToAnalyze.forEach(p => next.delete(p.id));
      return next;
    });

    showAlert(
      successfulSaves === 0 && failures > 0 ? 'Error' : 'Success',
      `Bulk analysis complete! ${successfulSaves} descriptions generated and saved${failures ? `, ${failures} failed (see console)` : ''}.`
    );
  };

  const toggleProductSelection = (productId, e) => {
    e.stopPropagation(); // Prevent product from being opened in main view
    setSelectedForBulk(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const toggleAllSelection = (filteredProducts) => {
    const unanalyzedInView = filteredProducts.filter(p => !p.ai_description);
    if (unanalyzedInView.every(p => selectedForBulk.has(p.id))) {
      // If all unanalyzed are selected, clear selection for these products
      setSelectedForBulk(prev => {
        const newSet = new Set(prev);
        unanalyzedInView.forEach(p => newSet.delete(p.id));
        return newSet;
      });
    } else {
      // Select all unanalyzed in view
      setSelectedForBulk(prev => {
        const newSet = new Set(prev);
        unanalyzedInView.forEach(p => newSet.add(p.id));
        return newSet;
      });
    }
  };

  const handleRefineDescription = async () => {
    if (!selectedProductId || !analysisResult || !refinementFeedback.trim()) return;
    setIsRefining(true);
    try {
      const res = await authFetch('/api/refine-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: selectedProductId, currentDescription: analysisResult, feedback: refinementFeedback })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Server responded with ${res.status}`);
      }

      const data = await res.json();
      const newAnalysisText = data.analysis || 'Refinement failed.';

      // AUTO-SAVE the refinement
      const saveRes = await authFetch('/api/save-ai-description', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ productId: selectedProductId, aiDescription: newAnalysisText })
      });

      if (!saveRes.ok) {
        const saveErrorData = await saveRes.json().catch(() => ({}));
        throw new Error(saveErrorData.error || 'Failed to auto-save the refined description.');
      }

      setAnalysisResult(newAnalysisText);
      // Update local product state
      setProducts(prev => prev.map(p => p.id === selectedProductId ? { ...p, ai_description: newAnalysisText } : p));

      setRefinementFeedback(''); // clear input after success
      showAlert('Success', 'Description refined and updated successfully.');
    } catch (err) {
      console.error('Refinement error:', err);
      showAlert('Error', 'Failed to refine description: ' + err.message);
    } finally {
      setIsRefining(false);
    }
  };

  // ... (rest of the existing logic for languages, welcome messages, etc.)
  const languages = [
    { code: 'en-IN', name: 'English', nativeName: 'English' },
    { code: 'hi-IN', name: 'Hindi', nativeName: 'हिंदी' },
    { code: 'bn-IN', name: 'Bengali', nativeName: 'বাংলা' },
    { code: 'gu-IN', name: 'Gujarati', nativeName: 'ગુજરાતી' },
    { code: 'kn-IN', name: 'Kannada', nativeName: 'ಕನ್ನಡ' },
    { code: 'ml-IN', name: 'Malayalam', nativeName: 'മലയാളം' },
    { code: 'mr-IN', name: 'Marathi', nativeName: 'मराठी' },
    { code: 'od-IN', name: 'Odia', nativeName: 'ଓଡ଼ିଆ' },
    { code: 'pa-IN', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
    { code: 'ta-IN', name: 'Tamil', nativeName: 'தமிழ்' },
    { code: 'te-IN', name: 'Telugu', nativeName: 'తెలుగు' },
    { code: 'ur-IN', name: 'Urdu', nativeName: 'اردو' },
    { code: 'as-IN', name: 'Assamese', nativeName: 'অসমীয়া' },
    { code: 'brx-IN', name: 'Bodo', nativeName: 'बड़ो' },
    { code: 'doi-IN', name: 'Dogri', nativeName: 'डोगरी' },
    { code: 'ks-IN', name: 'Kashmiri', nativeName: 'کٲشُر' },
    { code: 'kok-IN', name: 'Konkani', nativeName: 'कोंकणी' },
    { code: 'mai-IN', name: 'Maithili', nativeName: 'मैथिली' },
    { code: 'mni-IN', name: 'Manipuri', nativeName: 'মেইতেই' },
    { code: 'ne-IN', name: 'Nepali', nativeName: 'नेपाली' },
    { code: 'sa-IN', name: 'Sanskrit', nativeName: 'संस्कृतम्' },
    { code: 'sat-IN', name: 'Santali', nativeName: 'ᱥᱟᱱᱛᱟᱲᱤ' },
    { code: 'sd-IN', name: 'Sindhi', nativeName: 'سنڌي' },
  ];

  // English source strings — kept clean (no ₹ or ellipsis) so Sarvam translates them reliably
  const EN_WELCOME = 'Welcome to Indriya! How can I help you find the perfect jewellery today?';
  const EN_PLACEHOLDER = 'For example, Diamond rings under 50000 rupees';

  const [currentWelcome, setCurrentWelcome] = useState(EN_WELCOME);
  const [currentPlaceholder, setCurrentPlaceholder] = useState('E.g., Diamond rings under ₹50,000...');
  const [isTranslating, setIsTranslating] = useState(false);

  // Fetch translations from Sarvam whenever language changes
  useEffect(() => {
    let cancelled = false;
    const fetchTranslations = async () => {
      if (language === 'en-IN') {
        setCurrentWelcome(EN_WELCOME);
        setCurrentPlaceholder('E.g., Diamond rings under ₹50,000...');
        return;
      }
      setIsTranslating(true);
      try {
        const res = await fetch('/api/translate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            texts: [EN_WELCOME, EN_PLACEHOLDER],
            targetLanguage: language
          })
        });
        if (!res.ok) throw new Error('Translation request failed');
        const data = await res.json();
        if (!cancelled && data.translations && data.translations.length === 2) {
          setCurrentWelcome(data.translations[0]);
          setCurrentPlaceholder(data.translations[1]);
        }
      } catch (err) {
        console.error('Translation error:', err);
        // Gracefully fall back to English
        if (!cancelled) {
          setCurrentWelcome(EN_WELCOME);
          setCurrentPlaceholder(EN_PLACEHOLDER);
        }
      } finally {
        if (!cancelled) setIsTranslating(false);
      }
    };
    fetchTranslations();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const selectedLangObj = languages.find(l => l.code === language) || languages[1];

  // Auto-scroll chat: Scroll to top of bot responses, otherwise scroll to bottom
  useEffect(() => {
    if (chatLogRef.current) {
      const messageElements = chatLogRef.current.querySelectorAll('.message');
      if (messageElements.length > 0) {
        const lastMessage = messageElements[messageElements.length - 1];
        if (lastMessage.classList.contains('bot-message')) {
          lastMessage.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
        }
      }
    }
  }, [messages, isLoading]);

  const sendMessage = useCallback(async (overrideInput) => {
    const messageToSend = overrideInput || input;
    if (!messageToSend.trim()) return;

    const userMessage = { role: 'user', text: messageToSend };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: messageToSend, language: language, sessionId: currentSessionId, userId: selectedUser?.id }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      const botMessage = {
        role: 'bot',
        text: data.response,
        products: data.products
      };

      setMessages((prev) => [...prev, botMessage]);

      if (data.sessionId) {
        // Auto-update URL and refresh sidebar when a new session is created on first message
        if (!currentSessionId) {
            setCurrentSessionId(data.sessionId);
            navigate(`/chat/${data.sessionId}`);
            fetchSessions();
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages((prev) => [...prev, { role: 'bot', text: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, language, selectedUser, currentSessionId, navigate, fetchSessions]);

  // Initialize MediaRecorder for Sarvam AI Transcription
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const silenceTimeoutRef = useRef(null);
  const audioContextRef = useRef(null);
  const isListeningRef = useRef(false);

  const stopListening = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsListening(false);
    isListeningRef.current = false;

    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(console.error);
      audioContextRef.current = null;
    }
  }, []);

  const toggleListening = async () => {
    if (isListening) {
      stopListening();
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorderRef.current = new MediaRecorder(stream);
        audioChunksRef.current = [];
        isListeningRef.current = true;
        setIsListening(true);

        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorderRef.current.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const formData = new FormData();
          formData.append('audio', audioBlob, 'recording.webm');
          formData.append('languageCode', language);

          try {
            setInput('Transcribing...');
            const response = await fetch('/api/transcribe', {
              method: 'POST',
              body: formData,
            });

            if (!response.ok) throw new Error('Transcription failed');
            const data = await response.json();

            if (data.transcript) {
              setInput(data.transcript);
              setTimeout(() => sendMessage(data.transcript), 500);
            } else {
              setInput('');
            }
          } catch (error) {
            console.error('Transcription error:', error);
            setInput('');
            showAlert('Error', 'Failed to transcribe audio.');
          }

          stream.getTracks().forEach(track => track.stop());
        };

        mediaRecorderRef.current.start();

        // Silence detection
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(stream);
        microphone.connect(analyser);
        analyser.fftSize = 512;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        let lastSpeechTime = Date.now();

        const detectSilence = () => {
          if (!isListeningRef.current) return;

          analyser.getByteFrequencyData(dataArray);
          let sum = 0;
          for (let i = 0; i < bufferLength; i++) {
            sum += dataArray[i];
          }
          const average = sum / bufferLength;

          // Speech detected
          if (average > 15) {
            lastSpeechTime = Date.now();
          }

          // If 2 seconds of silence after speech, or 5 seconds of total silence, stop
          if (Date.now() - lastSpeechTime > 2000) {
            stopListening();
            return;
          }

          requestAnimationFrame(detectSilence);
        };

        detectSilence();

      } catch (err) {
        console.error('Microphone access denied:', err);
        showAlert('Microphone Error', 'Microphone access denied. Please enable it in browser settings.');
      }
    }
  };



  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  const handleLogin = async (selectedRole) => {
    if (selectedRole === 'admin') {
      if (adminTokenInput.trim() === '') {
        showAlert('Login Failed', 'Please enter the Admin token.');
        return;
      }
      const token = adminTokenInput.trim();
      const adminUser = usersList.find(u => u.role === 'admin') || { id: 1, name: 'Admin', role: 'admin', store_location: 'Head Office' };
      
      try {
        const response = await fetch('/api/admin-login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-token': token
          }
        });
        
        if (!response.ok) {
          showAlert('Login Failed', 'Invalid Admin token.');
          return;
        }

        login('admin', adminUser, token);
        setLanguage('en-IN');
        localStorage.setItem('language', 'en-IN');
        handleTabChange('chat');
      } catch (err) {
        showAlert('Login Failed', 'Network error.');
      }
    } else {
      if (!selectedUser) {
        showAlert('Login Failed', 'Please select a user profile.');
        return;
      }
      if (!userPassword.trim()) {
        showAlert('Login Failed', 'Please enter a password.');
        return;
      }

      try {
        const response = await fetch('/api/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ password: userPassword.trim() })
        });
        
        if (!response.ok) {
            showAlert('Login Failed', 'Invalid password.');
            return;
        }

        // Set default language based on store location
        const loc = selectedUser.store_location.toLowerCase();
        let defaultLang = 'hi-IN';
        if (loc.includes('chennai') || loc.includes('coimbatore')) defaultLang = 'ta-IN';
        else if (loc.includes('bangalore')) defaultLang = 'kn-IN';
        else if (loc.includes('hyderabad')) defaultLang = 'te-IN';
        else if (loc.includes('mumbai') || loc.includes('pune')) defaultLang = 'mr-IN';
        else if (loc.includes('ahmedabad')) defaultLang = 'gu-IN';
        else if (loc.includes('kochi') || loc.includes('ernakulam')) defaultLang = 'ml-IN';
        else if (loc.includes('kolkata')) defaultLang = 'bn-IN';
        
        setLanguage(defaultLang);
        localStorage.setItem('language', defaultLang);
        login('user', selectedUser);
        handleTabChange('chat');
      } catch (err) {
        showAlert('Login Failed', 'Network error.');
      }
    }
  };

  if (!role) {
    return (
      <div className="App" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-color)' }}>
        <CustomModal {...modalConfig} />
        <img src={optimizeImageUrl("https://s7ap1.scene7.com/is/content/noveljewelsprod/Indriya-Logo")} alt="Indriya Logo" style={{ width: '200px', marginBottom: '2rem' }} />
        <div style={{ background: 'var(--panel-bg)', padding: '2rem', borderRadius: '8px', border: '1px solid var(--border-light)', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', textAlign: 'center', width: '350px', maxWidth: '90%' }}>
          <h2 style={{ color: 'var(--primary-wine)', marginBottom: '2rem' }}>Welcome to Indriya AI</h2>
          <div style={{ marginBottom: '1.5rem', textAlign: 'left', position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-dark)' }}>Sales Team Login:</label>
            <div className="user-selector-wrapper" style={{ width: '100%', marginBottom: '1rem', position: 'relative' }}>
              <button
                type="button"
                className="custom-select-button"
                onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                style={{ width: '100%', border: '1px solid var(--border-light)', borderRadius: '4px', padding: '10px', background: 'white' }}
              >
                <span>{selectedUser ? `${selectedUser.name} - ${selectedUser.store_location}` : '-- Select your profile --'}</span>
                <span style={{ fontSize: '0.8em' }}>▼</span>
              </button>
              {isUserDropdownOpen && (
                <div
                  id="user-dropdown"
                  className="dropdown-menu show"
                  style={{ display: 'block' }}
                >
                  {usersList.filter(u => u.role === 'user').map(u => (
                    <div
                      key={u.id}
                      className={`dropdown-item ${selectedUser?.id === u.id ? 'selected' : ''}`}
                      onClick={() => {
                        setUser(u);
                        setIsUserDropdownOpen(false);
                      }}
                    >
                      {u.name} <br /><small style={{ color: 'var(--text-light)' }}>{u.store_location}</small>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => handleLogin('user')}
              style={{ padding: '10px 20px', width: '100%', background: 'var(--primary-wine)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              Continue as User
            </button>
            <input
              type="password"
              placeholder="User Password"
              value={userPassword}
              onChange={e => setUserPassword(e.target.value)}
              style={{ width: '100%', padding: '10px', marginTop: '1rem', borderRadius: '4px', border: '1px solid var(--border-light)', color: 'var(--text-dark)' }}
            />
          </div>
          <hr style={{ border: 'none', borderTop: '1px solid var(--border-light)', margin: '1.5rem 0' }} />
          <div style={{ textAlign: 'left' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-dark)' }}>Admin Access:</label>
            <input
              type="password"
              placeholder="Admin Token"
              value={adminTokenInput}
              onChange={e => setAdminTokenInput(e.target.value)}
              style={{ width: '100%', padding: '10px', marginBottom: '1rem', borderRadius: '4px', border: '1px solid var(--border-light)', color: 'var(--text-dark)' }}
            />
            <button
              onClick={() => handleLogin('admin')}
              style={{ padding: '10px 20px', width: '100%', background: 'transparent', color: 'var(--primary-wine)', border: '1px solid var(--primary-wine)', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              Login as Admin
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <CustomModal {...modalConfig} />
      <ImageLightbox isOpen={!!lightboxImage} imageUrl={lightboxImage} onClose={() => setLightboxImage(null)} />
      <div className="app-header-global" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {role && activeTab === 'chat' && (<button onClick={() => setIsSidebarOpen(!isSidebarOpen)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', marginRight: '15px', padding: 0, outline: 'none', display: 'flex', alignItems: 'center' }}><Menu size={24} color="var(--primary-wine)" /></button>)}
        <img src={optimizeImageUrl("https://s7ap1.scene7.com/is/content/noveljewelsprod/Indriya-Logo")} alt="Indriya Logo" className="header-logo" />
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <span style={{ color: 'var(--primary-wine)', fontWeight: 'bold' }}>{role === 'admin' ? 'Admin Mode' : 'User Mode'}</span>
          <button
            onClick={() => { logout(); setSessions([]); setMessages([]); setCurrentSessionId(null); navigate('/login'); }}
            style={{ padding: '5px 10px', background: 'transparent', color: 'var(--primary-wine)', border: '1px solid var(--primary-wine)', borderRadius: '4px', cursor: 'pointer' }}
          >
            Logout
          </button>
        </div>
      </div>
      <div className="app-tabs desktop-tabs">
        <button
          className={`tab-btn ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => { handleTabChange('chat'); }}
        >
          AI Chat Search
        </button>
        <button
          className={`tab-btn ${activeTab === 'visual' ? 'active' : ''}`}
          onClick={() => { handleTabChange('visual'); }}
        >
          Visual Search
        </button>
        {role === 'admin' && (
          <>
            <button
              className={`tab-btn ${activeTab === 'analysis' ? 'active' : ''}`}
              onClick={() => { handleTabChange('analysis'); }}
            >
              Product AI Analysis
            </button>
            <button
              className={`tab-btn ${activeTab === 'knowledge' ? 'active' : ''}`}
              onClick={() => { handleTabChange('knowledge'); }}
            >
              Knowledge Base Upload
            </button>
            <button
              className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => { handleTabChange('dashboard'); }}
            >
              Admin Dashboard
            </button>
          </>
        )}
      </div>

      {(!(activeTab === 'analysis' && selectedProductId)) && (
        <div className="mobile-bottom-nav">
        <button
          className={`nav-item ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => handleTabChange('chat')}
        >
          <span className="nav-icon"><MessageCircle size={20} /></span>
          <span className="nav-label">Search</span>
        </button>
        <button
          className={`nav-item ${activeTab === 'visual' ? 'active' : ''}`}
          onClick={() => handleTabChange('visual')}
        >
          <span className="nav-icon"><Eye size={20} /></span>
          <span className="nav-label">Visual</span>
        </button>
        {role === 'admin' && (
          <>
            <button
              className={`nav-item ${activeTab === 'analysis' ? 'active' : ''}`}
              onClick={() => handleTabChange('analysis')}
            >
              <span className="nav-icon"><Sparkles size={20} /></span>
              <span className="nav-label">Analyze</span>
            </button>
            <button
              className={`nav-item ${activeTab === 'knowledge' ? 'active' : ''}`}
              onClick={() => handleTabChange('knowledge')}
            >
              <span className="nav-icon"><BookOpen size={20} /></span>
              <span className="nav-label">Knowledge</span>
            </button>
            <button
              className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => handleTabChange('dashboard')}
            >
              <span className="nav-icon"><BarChart2 size={20} /></span>
              <span className="nav-label">Dashboard</span>
            </button>
          </>
        )}
      </div>
      )}

      {activeTab === 'chat' ? (

        <div className="chat-layout">
          {role && isSidebarOpen && (
            <div className="chat-sidebar" style={{
              position: windowWidth >= 768 ? 'relative' : 'absolute',
              top: 0,
              left: 0,
              bottom: windowWidth < 768 ? '80px' : '0',
              width: '280px',
              zIndex: 100,
              borderRight: '1px solid var(--border-light)',
              overflowY: 'auto',
              background: 'var(--panel-bg)',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: windowWidth >= 768 ? 'none' : '10px 0 30px rgba(0,0,0,0.1)',
              animation: 'slideIn 0.3s ease-out'
            }}>
              <button className="new-chat-btn" onClick={handleNewChat}>
                <Plus size={16} /> New Chat
              </button>
              <div className="session-list">
                {sessions.map(s => (
                  <div
                    key={s.id}
                    className={`session-item ${currentSessionId === s.id ? 'active' : ''}`}
                    onClick={() => navigate(`/chat/${s.id}`)}
                  >
                    <div className="session-item-content">
                      <MessageSquare size={16} color="var(--primary-wine)" className="session-icon" />
                      <span className="session-title">
                        {s.title}
                      </span>
                    </div>
                    <div className="session-date">
                      {new Date(s.updated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="chat-main-area" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div className="chat-container">
              <div className="chat-log" ref={chatLogRef}>
                {messages.length === 0 && (
                  <div className="welcome-message">
                    {currentWelcome}
                  </div>
                )}
                {messages.map((msg, index) => (
                  <div key={index} className={`message ${msg.role}-message`}>
                    <div className="message-content">
                      <strong>{msg.role === 'user' ? 'You' : 'Bot'}:</strong>
                      <div className="markdown-body chat-markdown" style={{ marginTop: '8px' }}>
                        <ReactMarkdown>{msg.text}</ReactMarkdown>
                      </div>

                      {msg.products && msg.products.length > 0 && (
                        <div className="product-gallery">
                          {msg.products.map((product, pIndex) => (
                            <ProductCard
                              key={pIndex}
                              product={product}
                              onCardClick={(p) => {
                                if (selectedUser?.id) {
                                  fetch('/api/log-click', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ userId: selectedUser.id, productId: p.id, productName: p.name })
                                  }).catch(e => console.error("Failed to log click:", e));
                                }
                              }}
                              onImageClick={(p) => setLightboxImage(p.image_urls ? p.image_urls.split(', ')[0] : null)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="message bot-message">
                    <div className="message-content">
                      <strong>Bot:</strong>
                      <div className="skeleton-typing-label">Finding jewellery for you...</div>
                      <div className="product-gallery skeleton-gallery">
                        {[1, 2, 3, 4].map(i => <SkeletonProductCard key={i} />)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="input-area">
                <div className="language-selector-wrapper">
                  <button
                    type="button"
                    className="custom-select-button"
                    onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                    disabled={isListening}
                    title="Select Language"
                  >
                    <span>{selectedLangObj.nativeName} ({selectedLangObj.name})</span>
                    <span style={{ fontSize: '0.8em' }}>▼</span>
                  </button>
                  {isLangDropdownOpen && (
                    <div
                      id="language-dropdown"
                      className="dropdown-menu show"
                      style={{ display: 'block' }}
                    >
                      {languages.map((lang) => (
                        <div
                          key={lang.code}
                          className={`dropdown-item ${language === lang.code ? 'selected' : ''}`}
                          onClick={() => {
                            setLanguage(lang.code);
                            setIsLangDropdownOpen(false);
                          }}
                        >
                          {lang.nativeName} <span style={{ color: '#888', fontSize: '0.85em' }}>({lang.name})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  className={`voice-btn ${isListening ? 'listening' : ''}`}
                  onClick={toggleListening}
                  title="Search with Voice"
                >
                  {isListening ? <StopIcon /> : <MicIcon />}
                </button>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={isTranslating ? 'Translating...' : currentPlaceholder}
                  disabled={isLoading || isTranslating}
                />
                <button className="send-button" onClick={() => sendMessage()} disabled={isLoading || !input.trim()}>
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === 'visual' ? (
        <VisualSearch />
      ) : activeTab === 'analysis' ? (
        <div className={`analysis-container ${selectedProductId ? 'has-selection' : ''}`} style={{ paddingBottom: windowWidth < 768 ? '100px' : '0' }}>
          <div className={`analysis-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`} style={{ position: 'relative' }}>
            <button 
              className="sidebar-toggle-btn"
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {isSidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>
            <div className="sidebar-header-area" style={{ padding: '24px', borderBottom: '1px solid var(--border-light)' }}>
              <h3 style={{ margin: '0 0 16px 0', color: 'var(--primary-wine)' }}>Product Intelligence</h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ position: 'relative' }}>
                  <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                  <input
                    type="text"
                    placeholder="Search name or SKU..."
                    value={analysisSearch}
                    onChange={(e) => setAnalysisSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 10px 10px 36px',
                      borderRadius: '10px',
                      border: '1px solid var(--border-light)',
                      fontSize: '0.9em',
                      background: 'var(--bg-color)',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.75em', color: 'var(--text-light)', fontWeight: 'bold', letterSpacing: '0.5px' }}>SORT BY:</span>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <button
                      onClick={() => setIsAnalysisSortOpen(!isAnalysisSortOpen)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-light)',
                        fontSize: '0.85em',
                        background: 'white',
                        color: 'var(--text-dark)',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.02)',
                        transition: 'all 0.2s'
                      }}
                    >
                      <span style={{ fontWeight: '600' }}>
                        {analysisSort === 'newest' && 'Newest First'}
                        {analysisSort === 'oldest' && 'Oldest First'}
                        {analysisSort === 'name' && 'Product Name (A-Z)'}
                        {analysisSort === 'sku' && 'SKU Code'}
                        {analysisSort === 'price-asc' && 'Price (Low to High)'}
                        {analysisSort === 'price-desc' && 'Price (High to Low)'}
                        {analysisSort === 'analyzed' && 'Analyzed First'}
                      </span>
                      <ChevronDown size={14} style={{ transform: isAnalysisSortOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                    </button>

                    {isAnalysisSortOpen && (
                      <>
                        <div
                          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100 }}
                          onClick={() => setIsAnalysisSortOpen(false)}
                        />
                        <div style={{
                          position: 'absolute',
                          top: 'calc(100% + 5px)',
                          left: 0,
                          right: 0,
                          background: 'white',
                          borderRadius: '12px',
                          border: '1px solid var(--border-light)',
                          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                          zIndex: 101,
                          overflow: 'hidden',
                          animation: 'fadeIn 0.2s ease'
                        }}>
                          {[
                            { val: 'newest', label: 'Newest First' },
                            { val: 'oldest', label: 'Oldest First' },
                            { val: 'name', label: 'Product Name (A-Z)' },
                            { val: 'sku', label: 'SKU Code' },
                            { val: 'price-asc', label: 'Price (Low to High)' },
                            { val: 'price-desc', label: 'Price (High to Low)' },
                            { val: 'analyzed', label: 'Analyzed First' }
                          ].map(opt => (
                            <div
                              key={opt.val}
                              onClick={() => { setAnalysisSort(opt.val); setIsAnalysisSortOpen(false); }}
                              style={{
                                padding: '10px 14px',
                                fontSize: '0.85em',
                                cursor: 'pointer',
                                color: analysisSort === opt.val ? 'var(--primary-wine)' : 'var(--text-dark)',
                                background: analysisSort === opt.val ? 'rgba(192, 154, 103, 0.1)' : 'transparent',
                                fontWeight: analysisSort === opt.val ? 'bold' : 'normal',
                                transition: 'all 0.2s'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(192, 154, 103, 0.05)'}
                              onMouseOut={(e) => e.currentTarget.style.background = analysisSort === opt.val ? 'rgba(192, 154, 103, 0.1)' : 'transparent'}
                            >
                              {opt.label}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <button
                  onClick={handleBulkAnalyze}
                  disabled={isBulkAnalyzing}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: 'none',
                    background: isBulkAnalyzing ? '#f0f0f0' : 'var(--primary-wine)',
                    color: isBulkAnalyzing ? '#999' : 'white',
                    fontSize: '0.85em',
                    fontWeight: '600',
                    cursor: isBulkAnalyzing ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginTop: '4px',
                    boxShadow: '0 4px 10px rgba(114, 18, 38, 0.15)',
                    transition: 'all 0.2s'
                  }}
                >
                  <Sparkles size={14} />
                  {isBulkAnalyzing ? `Analyzing ${bulkAnalysisProgress.current}/${bulkAnalysisProgress.total}...` : 'Analyze Selected'}
                </button>
              </div>
            </div>

            <div className="analysis-product-list">
              {(() => {
                const filteredProducts = products;

                const unanalyzedInView = filteredProducts.filter(p => !p.ai_description);
                const allSelected = unanalyzedInView.length > 0 && unanalyzedInView.every(p => selectedForBulk.has(p.id));

                return (
                  <>
                    <div
                      className="select-all-row"
                      onClick={() => totalUnanalyzedCount > 0 && toggleAllSelection(filteredProducts)}
                      style={{ opacity: totalUnanalyzedCount === 0 ? 0.5 : 1, cursor: totalUnanalyzedCount === 0 ? 'default' : 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => { }} // Controlled by row click
                        disabled={totalUnanalyzedCount === 0}
                        style={{ cursor: 'pointer', accentColor: 'var(--primary-wine)' }}
                      />
                      <span>Select all unanalyzed</span>
                      <div style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--text-light)', fontWeight: 'bold' }}>
                        {totalUnanalyzedCount} ITEMS
                      </div>
                    </div>
                    {filteredProducts.map(p => (
                      <div
                        key={p.id}
                        className={`analysis-product-item ${selectedProductId === p.id ? 'selected' : ''} ${p.ai_description ? 'analyzed' : ''}`}
                        onClick={() => handleProductSelect(p.id)}
                        title={p.name}
                        style={{ position: 'relative' }}
                      >
                        {!p.ai_description && (
                          <div
                            className="item-selection-overlay"
                            onClick={(e) => toggleProductSelection(p.id, e)}
                          >
                            <input
                              type="checkbox"
                              checked={selectedForBulk.has(p.id)}
                              onChange={() => { }} // Handle change on the div wrapper
                              style={{ cursor: 'pointer', accentColor: 'var(--primary-wine)' }}
                            />
                          </div>
                        )}
                        <img src={p.image_urls ? optimizeImageUrl(p.image_urls.split(', ')[0], 200) : NO_IMAGE_PLACEHOLDER} alt="" draggable="false" loading="lazy" style={{ marginLeft: !p.ai_description ? '30px' : '0' }} />
                        <div className="item-info" style={{ position: 'relative' }}>
                          <p className="item-name">{p.name}</p>
                          <p className="item-sku">{p.sku}</p>
                          {analyzingProductIds.has(p.id) ? (
                            <div style={{ position: 'absolute', bottom: '2px', right: '0px', color: 'var(--primary-wine)' }}>
                              <Loader2 size={16} className="animate-spin" />
                            </div>
                          ) : p.ai_description ? (
                            <span className="analyzed-badge saved"><Check size={12} style={{ marginRight: '4px' }} /> Analyzed</span>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </>
                );
              })()}
              {hasMoreProducts && (
                <button
                  onClick={loadMoreProducts}
                  disabled={isLoadingProducts}
                  style={{ width: '100%', padding: '10px', marginTop: '10px', background: 'var(--primary-wine)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  {isLoadingProducts ? 'Loading...' : 'Load More'}
                </button>
              )}
            </div>
          </div>
          <div className="analysis-main">
            {selectedProductId ? (
              <div className="analysis-active-view">
                <div className="analysis-top-controls">
                  <button
                    className="mobile-back-btn"
                    onClick={() => setSelectedProductId(null)}
                  >
                    ← Back to Products
                  </button>
                  {windowWidth >= 768 && (
                    <button 
                      className="collapse-image-btn"
                      onClick={() => setIsImageCollapsed(!isImageCollapsed)}
                    >
                      {isImageCollapsed ? (
                        <><span>Show Image</span></>
                      ) : (
                        <><span>Collapse Image</span></>
                      )}
                    </button>
                  )}
                </div>

                <div className={`analysis-split-view ${isImageCollapsed ? 'image-collapsed' : ''}`}>
                  {(windowWidth >= 768 || activeAnalysisSubTab === 'gallery') && (
                    <div className="analysis-image-gallery bento-item" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

                    {(() => {
                      const p = products.find(prod => prod.id === selectedProductId);
                      if (!p || !p.image_urls) return null;
                      const images = p.image_urls.split(', ').map(u => u.trim());
                      return (
                        <>
                          <div 
                            className="gallery-main-image-wrapper" 
                            onClick={() => setLightboxImage(images[analysisImageIndex] || images[0])}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setLightboxImage(images[analysisImageIndex] || images[0]);
                              }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label="Zoom product main image"
                            style={{ position: 'relative', width: '100%', aspectRatio: '1', borderRadius: '16px', overflow: 'hidden', border: '1px solid var(--border-light)', background: '#fff', cursor: 'zoom-in' }}
                          >
                            <img src={optimizeImageUrl(images[analysisImageIndex] || images[0])} alt="Product Main" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            {images.length > 1 && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAnalysisImageIndex(prev => (prev - 1 + images.length) % images.length);
                                  }}
                                  style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', zIndex: 10, padding: 0 }}
                                >
                                  <ChevronLeft size={24} style={{ color: 'var(--text-dark)' }} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAnalysisImageIndex(prev => (prev + 1) % images.length);
                                  }}
                                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', zIndex: 10, padding: 0 }}
                                >
                                  <ChevronRight size={24} style={{ color: 'var(--text-dark)' }} />
                                </button>
                              </>
                            )}
                          </div>
                          {images.length > 1 && (
                            <div className="gallery-thumbnails" style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '10px' }}>
                              {images.map((url, i) => (
                                <div
                                  key={i}
                                  onClick={() => setAnalysisImageIndex(i)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setAnalysisImageIndex(i);
                                    }
                                  }}
                                  tabIndex={0}
                                  role="button"
                                  aria-label={`Select product image thumbnail ${i + 1}`}
                                  style={{
                                    width: '60px',
                                    height: '60px',
                                    flexShrink: 0,
                                    borderRadius: '8px',
                                    border: analysisImageIndex === i ? '2px solid var(--primary-wine)' : '1px solid var(--border-light)',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    background: '#fff'
                                  }}
                                >
                                  <img src={optimizeImageUrl(url, 200)} alt={`Thumb ${i}`} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  )}

                  {(windowWidth >= 768 || activeAnalysisSubTab === 'report') && (
                  <div className="analysis-text-side bento-item" style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>

                    {(() => {
                      const p = products.find(prod => prod.id === selectedProductId);
                      if (!p) return null;
                      return (
                        <div className="product-details-header">
                          <div className="details-header-main">
                            <h2>{p.name}</h2>
                            <p className="sku-price">
                              <span className="sku-badge">SKU: {p.sku}</span>
                              <span className="price-tag">₹{Number(p.price).toLocaleString()}</span>
                            </p>
                          </div>
                          <div className="details-header-actions">
                            {analysisResult && (
                              <button
                                className={`analyze-btn-premium ${analyzingProductIds.has(selectedProductId) ? 'pulsing' : ''}`}
                                onClick={handleAnalyze}
                                disabled={analyzingProductIds.has(selectedProductId)}
                              >
                                {analyzingProductIds.has(selectedProductId) ? (
                                  'Curating Report...'
                                ) : (
                                  <>
                                    <Sparkles size={14} style={{ display: 'inline', marginRight: '6px' }} />
                                    Re-generate Report
                                  </>
                                )}
                              </button>
                            )}
                          </div>

                        </div>
                      );
                    })()}
                    <div className="analysis-display">
                      <div className="markdown-body" style={{ minHeight: '100%', position: 'relative' }}>
                        {analysisResult ? (
                          <>
                            {(() => {
                              const p = products.find(prod => prod.id === selectedProductId);
                              const structured = p?.structured_analysis;
                              if (!structured || /\{[\s\S]*\}/.test(analysisResult || '')) return null;
                              return (
                                <div className="structured-insights" style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                  
                                  {/* Intelligence Profile Grid */}
                                  <div className="intelligence-profile">
                                    {[
                                      { label: 'Metal & Purity', val: structured.metal, icon: <Layers size={14} /> },
                                      { label: 'Style & Vibe', val: structured.style, icon: <Zap size={14} /> },
                                      { label: 'Skin Tone', val: structured.skin_tone?.[0], icon: <User size={14} /> },
                                      { label: 'Face Shape', val: structured.face_shape, icon: <Layout size={14} /> },
                                      { label: 'Era', val: structured.design_era, icon: <History size={14} /> },
                                      { label: 'Richness', val: structured.richness_level, icon: <Crown size={14} /> }
                                    ].filter(item => item.val).map((item, idx) => (
                                      <div key={idx} className="profile-card">
                                        <div className="profile-card-header">
                                          {item.icon}
                                          <span>{item.label}</span>
                                        </div>
                                        <div className="profile-card-value">{item.val}</div>
                                      </div>
                                    ))}

                                    {structured.visual_identity && (
                                      <div className="profile-card full-span">
                                        <div className="profile-card-header">
                                          <Eye size={14} />
                                          <span>Visual Identity</span>
                                        </div>
                                        <div className="profile-card-value">{structured.visual_identity}</div>
                                      </div>
                                    )}

                                    {structured.heritage_cues && (
                                      <div className="profile-card full-span">
                                        <div className="profile-card-header">
                                          <Building2 size={14} />
                                          <span>Heritage Cues</span>
                                        </div>
                                        <div className="profile-card-value">{structured.heritage_cues}</div>
                                      </div>
                                    )}
                                    
                                    {structured.occasion && structured.occasion.length > 0 && (
                                      <div className="profile-card full-span">
                                        <div className="profile-card-header">
                                          <Calendar size={14} />
                                          <span>Perfect For Occasions</span>
                                        </div>
                                        <div className="profile-chips">
                                          {structured.occasion.map((occ, i) => (
                                            <span key={i} className="profile-chip">{occ}</span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  <div style={{ height: '1px', background: 'var(--border-light)', margin: '4px 0' }}></div>
                                  {structured.motifs && structured.motifs.length > 0 && (
                                    <div className="insight-group">
                                      <h4 style={{ fontSize: '0.7em', textTransform: 'uppercase', color: 'var(--primary-wine)', marginBottom: '8px', letterSpacing: '1px' }}>Design Motifs</h4>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {structured.motifs.map((m, i) => <span key={i} className="insight-badge motif-badge">{m}</span>)}
                                      </div>
                                    </div>
                                  )}
                                  {structured.craftsmanship && structured.craftsmanship.length > 0 && (
                                    <div className="insight-group">
                                      <h4 style={{ fontSize: '0.7em', textTransform: 'uppercase', color: 'var(--primary-wine)', marginBottom: '8px', letterSpacing: '1px' }}>Craftsmanship</h4>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {structured.craftsmanship.map((c, i) => <span key={i} className="insight-badge craft-badge">{c}</span>)}
                                      </div>
                                    </div>
                                  )}
                                  {structured.gemstones && structured.gemstones.length > 0 && (
                                    <div className="insight-group">
                                      <h4 style={{ fontSize: '0.7em', textTransform: 'uppercase', color: 'var(--primary-wine)', marginBottom: '8px', letterSpacing: '1px' }}>Gemstones</h4>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {structured.gemstones.map((g, i) => <span key={i} className="insight-badge gem-badge">{g}</span>)}
                                      </div>
                                    </div>
                                  )}
                                  <div style={{ height: '1px', background: 'var(--border-light)', margin: '8px 0' }}></div>
                                </div>
                              );
                            })()}
                             
                             {/* Premium Dossier Rendering */}
                             {/\{[\s\S]*\}/.test(analysisResult || '') && (
                               <JewelleryAnalysisDossier result={analysisResult} />
                             )}
                             
                             {/* Fallback to old rendering if no JSON detected */}
                             {!/\{[\s\S]*\}/.test(analysisResult || '') && (
                               <>
                                 <div style={{ marginTop: '20px', marginBottom: '15px' }}>
                                   <h4 style={{ fontSize: '0.7em', textTransform: 'uppercase', color: 'var(--primary-wine)', marginBottom: '8px', letterSpacing: '1px', opacity: 0.6 }}>Detailed Heritage Report</h4>
                                 </div>
                                 <ReactMarkdown>{stripStructuredData(analysisResult)}</ReactMarkdown>
                                 
                                 <MotifDisplay 
                                   motifDetails={products.find(p => p.id === selectedProductId)?.motif_details} 
                                   rawAnalysis={analysisResult} 
                                 />
                               </>
                             )}
                            {/* Spacer to prevent text from hiding behind the floating button */}
                            <div style={{ height: '40px' }}></div>
                          </>
                        ) : (
                          <div className="analysis-empty-state">
                            <div className="glow-icon"><Sparkles size={32} /></div>
                            <h3>Unlock Expert Insights</h3>
                            <p>Our AI is trained on centuries of Indian jewellery heritage. Generate a rich, narrative-driven report detailing the craftsmanship, motifs, and styling of this piece.</p>
                             <button
                               className={`analyze-btn-premium ${analyzingProductIds.has(selectedProductId) ? 'pulsing' : ''}`}
                               onClick={handleAnalyze}
                               disabled={analyzingProductIds.has(selectedProductId)}
                               style={{ marginTop: '20px', padding: '12px 30px' }}
                             >
                               {analyzingProductIds.has(selectedProductId) ? 'Curating Report...' : <><Sparkles size={16} style={{ display: 'inline', marginRight: '6px' }} /> Generate AI Heritage Report</>}
                             </button>
                          </div>
                        )}
                      </div>
                    </div>
                    

                  </div>
                  )}
                </div>

                {/* Floating Refine AI Panel - Copilot Style */}
                {analysisResult && !analyzingProductIds.has(selectedProductId) && (activeAnalysisSubTab === 'report' || windowWidth >= 768) && (
                  <div className={`refinement-copilot-container ${isRefineOpen ? 'open' : ''}`}>
                    {!isRefineOpen ? (
                      <button 
                        className="copilot-fab"
                        onClick={() => setIsRefineOpen(true)}
                        aria-label="Refine with AI Copilot"
                      >
                        <Sparkles size={20} />
                        <span className="fab-text">Refine with AI</span>
                      </button>
                    ) : (
                      <div className="copilot-dialog" ref={refinementCardRef}>
                        <div className="copilot-header">
                          <div className="copilot-title">
                            <BrainCircuit size={18} />
                            <span>Heritage Copilot</span>
                          </div>
                          <button className="copilot-close" onClick={() => setIsRefineOpen(false)}>×</button>
                        </div>
                        <div className="copilot-body">
                          <p className="copilot-hint">Refine the narrative, focus on specific motifs, or adjust the tone.</p>
                          <div className="copilot-input-wrapper">
                            <textarea
                              className="copilot-textarea"
                              placeholder="e.g. 'Describe the floral motifs in more detail' or 'Make the tone more regal'"
                              value={refinementFeedback}
                              onChange={(e) => setRefinementFeedback(e.target.value)}
                              disabled={isRefining}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleRefineDescription();
                                }
                              }}
                              autoFocus
                            />
                             <button
                               className="copilot-send-btn"
                               onClick={handleRefineDescription}
                               disabled={isRefining || !refinementFeedback.trim()}
                             >
                               {isRefining ? <Loader2 className="animate-spin" size={18} /> : 'SEND'}
                             </button>

                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Mobile Navigation Dock */}
                <div className="analysis-mobile-dock">
                  <button 
                    className={`dock-btn ${activeAnalysisSubTab === 'gallery' ? 'active' : ''}`}
                    onClick={() => setActiveAnalysisSubTab('gallery')}
                  >
                    <Eye size={20} />
                    <span>Visuals</span>
                  </button>
                  <button 
                    className={`dock-btn ${activeAnalysisSubTab === 'report' ? 'active' : ''}`}
                    onClick={() => setActiveAnalysisSubTab('report')}
                  >
                    <Sparkles size={20} />
                    <span>Intelligence</span>
                  </button>
                </div>
              </div>


            ) : (
              <div className="analysis-placeholder">
                <span className="placeholder-icon"><Diamond size={48} /></span>
                <h2>Premium Catalogue Intelligence</h2>
                <p>Select a product from the gallery above to explore its heritage, craftsmanship, and styling details using our expert AI.</p>
              </div>
            )}
          </div>
        </div>
      ) : activeTab === 'knowledge' ? (
        <div style={{ padding: windowWidth >= 768 ? '20px 40px 40px 40px' : '10px 20px 100px 20px', width: '100%', boxSizing: 'border-box', overflowY: 'auto', flex: 1 }}>


          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(${windowWidth >= 768 ? '450px' : '300px'}, 1fr))`,
            gap: windowWidth >= 768 ? '30px' : '20px',
            alignItems: 'start'
          }}>
            {/* Left Column: Education/Training */}
            <div style={{ position: windowWidth > 1000 ? 'sticky' : 'relative', top: '20px', width: '100%' }}>
              <div style={{
                background: 'white',
                padding: windowWidth >= 768 ? '35px' : '20px',
                borderRadius: '24px',
                border: '1px solid var(--border-light)',
                boxShadow: '0 10px 40px rgba(0,0,0,0.06)'
              }}>
                <div style={{ textAlign: 'center', marginBottom: '35px' }}>
                  <div style={{
                    width: windowWidth >= 768 ? '70px' : '55px',
                    height: windowWidth >= 768 ? '70px' : '55px',
                    background: 'linear-gradient(135deg, rgba(192, 154, 103, 0.2), rgba(114, 21, 48, 0.1))',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    color: 'var(--primary-wine)',
                    boxShadow: '0 4px 12px rgba(114, 21, 48, 0.1)'
                  }}>
                    <BrainCircuit size={windowWidth >= 768 ? 36 : 28} className={isUploadingKb ? 'pulse-animation' : ''} />
                  </div>
                  <h2 style={{ color: 'var(--primary-wine)', marginBottom: '12px', fontSize: windowWidth >= 768 ? '1.6em' : '1.3em' }}>Educate Your AI</h2>
                  <p style={{ color: 'var(--text-light)', fontSize: '0.9em', lineHeight: '1.6' }}>
                    Train your AI on technical spec sheets, catalogues, or brand portals.
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '30px' }}>
                  <div style={{
                    border: '2px dashed var(--border-light)',
                    borderRadius: '16px',
                    padding: '25px 20px',
                    textAlign: 'center',
                    background: kbFile ? 'rgba(192, 154, 103, 0.04)' : 'var(--bg-color)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }} onClick={() => document.getElementById('kb-file-input').click()}>
                    <input
                      id="kb-file-input"
                      type="file"
                      accept=".pdf,.txt,.csv,.xlsx,.xls,.doc,.docx,.pptx,.odt,.rtf"
                      onChange={(e) => setKbFile(e.target.files[0])}
                      style={{ display: 'none' }}
                    />
                    <div style={{ color: 'var(--primary-gold)', marginBottom: '12px' }}>
                      <Upload size={windowWidth >= 768 ? 28 : 22} />
                    </div>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '1em', color: 'var(--text-dark)' }}>{kbFile ? kbFile.name : 'Technical Documents'}</h4>
                    <p style={{ fontSize: '0.8em', color: 'var(--text-light)' }}>PDF, Word, Excel, PPT or Text files</p>
                  </div>

                  <div style={{
                    border: '1px solid var(--border-light)',
                    borderRadius: '16px',
                    padding: '20px',
                    background: 'var(--bg-color)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: 'var(--primary-wine)' }}>
                      <LinkIcon size={16} />
                      <span style={{ fontSize: '0.9em', fontWeight: 'bold' }}>Web Intelligence Source</span>
                    </div>
                    <input
                      type="text"
                      placeholder="https://brand.com/glossary"
                      value={kbLink}
                      onChange={(e) => setKbLink(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '10px',
                        border: '1px solid var(--border-light)',
                        fontSize: '0.95em',
                        boxSizing: 'border-box',
                        background: 'white'
                      }}
                    />
                  </div>
                </div>

                <button
                  onClick={handleKbUpload}
                  disabled={(!kbFile && !kbLink.trim()) || isUploadingKb}
                  style={{
                    width: '100%',
                    padding: '18px',
                    background: 'var(--primary-wine)',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '12px',
                    fontWeight: 'bold',
                    fontSize: '1.05em',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    boxShadow: '0 4px 15px rgba(114, 21, 48, 0.2)',
                    transition: 'all 0.3s ease',
                    opacity: ((!kbFile && !kbLink.trim()) || isUploadingKb) ? 0.6 : 1
                  }}
                >
                  {isUploadingKb ? (
                    <>
                      <div className="spinner-small" style={{ borderColor: 'white', borderTopColor: 'transparent' }}></div>
                      <span>Building...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles size={20} />
                      <span>Start Training Session</span>
                    </>
                  )}
                </button>

                {isUploadingKb && (
                  <div style={{ marginTop: '30px', padding: '15px', background: 'rgba(192, 154, 103, 0.05)', borderRadius: '16px', border: '1px solid rgba(192, 154, 103, 0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                      {['Reading', 'Vectorizing', 'Indexing', 'Complete'].map((step, i) => {
                        const lowerRes = kbUploadResult.toLowerCase();
                        const isCurrent =
                          (step === 'Reading' && lowerRes.includes('reading')) ||
                          (step === 'Vectorizing' && lowerRes.includes('converting')) ||
                          (step === 'Indexing' && lowerRes.includes('storing')) ||
                          (step === 'Complete' && lowerRes.includes('successfully'));

                        const isDone =
                          (step === 'Reading' && (lowerRes.includes('converting') || lowerRes.includes('storing') || lowerRes.includes('successfully'))) ||
                          (step === 'Vectorizing' && (lowerRes.includes('storing') || lowerRes.includes('successfully'))) ||
                          (step === 'Indexing' && lowerRes.includes('successfully'));

                        return (
                          <div key={i} style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
                            <div style={{
                              width: windowWidth >= 768 ? '32px' : '24px',
                              height: windowWidth >= 768 ? '32px' : '24px',
                              borderRadius: '50%',
                              background: isDone ? 'var(--primary-wine)' : (isCurrent ? 'white' : '#eee'),
                              border: `2px solid ${isDone || isCurrent ? 'var(--primary-wine)' : '#eee'}`,
                              margin: '0 auto 10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: isDone ? 'white' : (isCurrent ? 'var(--primary-wine)' : '#ccc'),
                              fontSize: windowWidth >= 768 ? '13px' : '10px',
                              fontWeight: 'bold',
                              zIndex: 2,
                              position: 'relative',
                              boxShadow: isCurrent ? '0 0 15px rgba(114, 21, 48, 0.3)' : 'none',
                              transition: 'all 0.4s ease'
                            }}>
                              {isDone ? <Check size={windowWidth >= 768 ? 16 : 12} /> : i + 1}
                            </div>
                            <span style={{
                              fontSize: windowWidth >= 768 ? '0.75em' : '0.6em',
                              color: isCurrent || isDone ? 'var(--text-dark)' : 'var(--text-light)',
                              fontWeight: isCurrent ? 'bold' : 'normal',
                              display: 'block',
                              transform: isCurrent ? 'scale(1.1)' : 'scale(1)',
                              transition: 'all 0.3s ease'
                            }}>{step}</span>
                            {i < 3 && (
                              <div style={{
                                position: 'absolute',
                                top: windowWidth >= 768 ? '16px' : '12px',
                                left: `calc(50% + ${windowWidth >= 768 ? '16px' : '12px'})`,
                                width: `calc(100% - ${windowWidth >= 768 ? '32px' : '24px'})`,
                                height: '2px',
                                background: isDone ? 'var(--primary-wine)' : '#eee',
                                zIndex: 1,
                                transition: 'all 0.6s ease'
                              }}></div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ textAlign: 'center', color: 'var(--primary-wine)', fontSize: '0.75em', fontWeight: '600' }}>
                      {kbUploadResult}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Active Intelligence */}
            <div style={{ width: '100%' }}>
              {isKbLoading ? (
                <div style={{ textAlign: 'center', padding: '60px', background: 'white', borderRadius: '24px', border: '1px solid var(--border-light)' }}>
                  <Loader2 size={48} className="spinner" style={{ color: 'var(--primary-wine)', opacity: 0.5, margin: '0 auto 20px' }} />
                  <p style={{ color: 'var(--text-light)' }}>Syncing Knowledge Inventory...</p>
                </div>
              ) : kbError ? (
                <div style={{ textAlign: 'center', padding: '60px', background: 'white', borderRadius: '24px', border: '1px solid #fee2e2', color: '#b91c1c' }}>
                  <div style={{ marginBottom: '15px' }}>
                    <BrainCircuit size={48} />
                  </div>
                  <h3 style={{ margin: '0 0 10px 0' }}>Access Restricted</h3>
                  <p style={{ fontSize: '0.9em', maxWidth: '300px', margin: '0 auto' }}>{kbError}</p>
                  <button onClick={fetchKbSources} style={{ marginTop: '20px', padding: '8px 16px', background: 'var(--primary-wine)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Retry Sync</button>
                </div>
              ) : kbSources.length === 0 ? (
                <div style={{ textAlign: 'center', padding: windowWidth >= 768 ? '60px' : '30px', background: 'white', borderRadius: '24px', border: '1px solid var(--border-light)' }}>
                  <div style={{ color: 'var(--text-light)', marginBottom: '15px' }}>
                    <BrainCircuit size={48} opacity={0.3} />
                  </div>
                  <h3 style={{ color: 'var(--text-light)', fontSize: '1.2em' }}>No active intelligence sources found.</h3>
                  <p style={{ color: 'var(--text-light)', fontSize: '0.9em' }}>Upload your first technical document to begin.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '30px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 style={{ color: 'var(--text-dark)', margin: 0, fontSize: windowWidth >= 768 ? '1.4em' : '1.2em' }}>Active Knowledge Inventory</h3>
                    <span style={{ fontSize: '0.85em', color: 'var(--text-light)', background: 'white', padding: '6px 14px', borderRadius: '20px', border: '1px solid var(--border-light)' }}>
                      {kbSources.length} Sources Integrated
                    </span>
                  </div>

                  {kbSources.map((source, idx) => (
                    <div key={idx} style={{
                      background: 'white',
                      borderRadius: '16px',
                      padding: windowWidth >= 768 ? '24px' : '20px',
                      border: '1px solid var(--border-light)',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                          <div style={{
                            width: windowWidth >= 768 ? '45px' : '38px',
                            height: windowWidth >= 768 ? '45px' : '38px',
                            background: 'rgba(192, 154, 103, 0.1)',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--primary-wine)',
                            flexShrink: 0
                          }}>
                            <FileText size={windowWidth >= 768 ? 22 : 18} />
                          </div>
                          <div style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
                            <strong style={{ 
                              color: 'var(--text-dark)', 
                              fontSize: windowWidth >= 768 ? '1.1em' : '0.95em', 
                              display: 'block', 
                              marginBottom: '2px', 
                              wordBreak: 'break-all',
                              overflowWrap: 'anywhere'
                            }}>{source.source_name}</strong>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>

                              <span style={{ fontSize: '0.75em', color: 'var(--text-light)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'green' }}></div>
                                {source.chunks} Chunks
                              </span>
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', width: windowWidth >= 768 ? 'auto' : '100%', flexShrink: 0, justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => handleKbPreview(source.source_name)}
                            disabled={isDownloadingPreview}
                            style={{
                              flex: 1,
                              padding: windowWidth >= 768 ? '8px 18px' : '6px 12px',
                              background: selectedKbPreview?.sourceName === source.source_name ? 'var(--primary-wine)' : 'transparent',
                              color: selectedKbPreview?.sourceName === source.source_name ? 'white' : 'var(--primary-wine)',
                              border: '1px solid var(--primary-wine)',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: windowWidth >= 768 ? '0.85em' : '0.75em',
                              fontWeight: '600',
                              transition: 'all 0.2s',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {isDownloadingPreview && selectedKbPreview?.sourceName === source.source_name ? '...' : (selectedKbPreview?.sourceName === source.source_name ? 'Hide' : 'Inspect')}
                          </button>
                          <button
                            onClick={() => handleKbDelete(source.source_name)}
                            style={{
                              padding: windowWidth >= 768 ? '8px 18px' : '6px 12px',
                              background: 'transparent',
                              color: '#d32f2f',
                              border: '1px solid rgba(211, 47, 47, 0.2)',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '0.85em'
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      {selectedKbPreview?.sourceName === source.source_name && (
                        <div style={{
                          width: '100%',
                          marginTop: '20px',
                          background: 'var(--bg-color)',
                          borderRadius: '16px',
                          border: '1px solid var(--border-light)',
                          padding: windowWidth >= 768 ? '24px' : '16px',
                          maxHeight: '600px',
                          overflowY: 'auto',
                          boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.04)',
                          animation: 'fadeIn 0.4s ease'
                        }}>
                          <div style={{
                            fontSize: windowWidth >= 768 ? '1em' : '0.9em',
                            color: 'var(--text-dark)',
                            lineHeight: '1.9',
                            whiteSpace: 'pre-wrap',
                            textAlign: 'left',
                            fontFamily: 'Inter, system-ui, sans-serif'
                          }}>
                            {selectedKbPreview.fullContent || 'Reconstructing intelligence...'}
                          </div>
                        </div>
                      )}

                      {!(selectedKbPreview?.sourceName === source.source_name) && source.summary && (
                        <div style={{
                          fontSize: '0.85em',
                          color: 'var(--text-dark)',
                          background: 'rgba(192, 154, 103, 0.04)',
                          padding: windowWidth >= 768 ? '16px 20px' : '12px 16px',
                          borderRadius: '12px',
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid rgba(192, 154, 103, 0.1)',
                          marginTop: '15px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--primary-wine)', marginBottom: '8px', fontWeight: 'bold', fontSize: '0.75em', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            <Sparkles size={14} /> Intelligence Context
                          </div>
                          <div className="markdown-body" style={{ fontSize: '0.9em', color: 'var(--text-dark)' }}>
                            <ReactMarkdown>{source.summary}</ReactMarkdown>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="dashboard-container" style={{ padding: windowWidth >= 768 ? '2rem' : '1rem 1rem 100px 1rem', color: 'var(--text-dark)', width: '100%', overflowY: 'auto', flex: 1, boxSizing: 'border-box' }}>
          <h2 style={{ color: 'var(--primary-wine)' }}>Admin Dashboard</h2>

          <div className="dashboard-grid">
            {/* Top Clicked Products */}
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <span className="dashboard-card-icon"><Crown size={24} /></span>
                <h3>Top Clicked Products</h3>
              </div>
              <ul className="dashboard-list">
                {adminMetrics.topProducts?.map((p, i) => (
                  <li key={i} className="dashboard-list-item">
                    <span className="dashboard-item-title">{p.product_name}</span>
                    <span className="dashboard-stat-badge">{p.clicks} clicks</span>
                  </li>
                ))}
                {adminMetrics.topProducts.length === 0 && <p style={{ color: 'var(--text-light)' }}>No clicks recorded yet.</p>}
              </ul>
            </div>

            {/* Store Performance (Conversion & Engagement) */}
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <span className="dashboard-card-icon"><Store size={24} /></span>
                <h3>Store Performance & Engagement</h3>
              </div>
              <ul className="dashboard-list">
                {adminMetrics.storePerformance?.map((s, i) => (
                  <li key={i} className="dashboard-list-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span className="dashboard-item-title">{s.store_location}</span>
                      {/* Ratio, not a percentage: a click is not tied to the search that
                          produced it, so this is unbounded and cannot be a "rate". */}
                      <span className="dashboard-stat-badge" style={{ background: Number(s.clicks_per_search) >= 0.2 ? 'rgba(0,128,0,0.1)' : 'rgba(192, 154, 103, 0.1)', color: Number(s.clicks_per_search) >= 0.2 ? 'green' : 'var(--primary-wine)' }}>
                        {(parseFloat(s.clicks_per_search) || 0).toFixed(2)} clicks/search
                      </span>
                    </div>
                    <div style={{ fontSize: '0.8em', color: 'var(--text-light)' }}>
                      {s.searches} Searches • {s.clicks} Clicks
                    </div>
                  </li>
                ))}
                {adminMetrics.storePerformance.length === 0 && <p style={{ color: 'var(--text-light)' }}>No store activity yet.</p>}
                {adminMetrics.inactiveStoreCount > 0 && (
                  <p style={{ color: 'var(--text-light)', fontSize: '0.8em', marginTop: '8px' }}>
                    {adminMetrics.inactiveStoreCount} more {adminMetrics.inactiveStoreCount === 1 ? 'store has' : 'stores have'} no recorded activity.
                  </p>
                )}
                {/* Internal traffic, shown but kept out of the store ranking above so it
                    cannot be mistaken for a store's numbers. */}
                {adminMetrics.adminActivity && (adminMetrics.adminActivity.searches > 0 || adminMetrics.adminActivity.clicks > 0) && (
                  <li className="dashboard-list-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px', marginTop: '8px', borderTop: '1px dashed var(--border-light)', paddingTop: '10px', opacity: 0.75 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span className="dashboard-item-title">
                        Internal / Admin
                        <span style={{ fontSize: '0.75em', color: 'var(--text-light)', fontWeight: 'normal', marginLeft: '6px' }}>
                          not a store • excluded from ranking
                        </span>
                      </span>
                      <span className="dashboard-stat-badge" style={{ background: 'rgba(0,0,0,0.05)', color: 'var(--text-light)' }}>
                        {(parseFloat(adminMetrics.adminActivity.clicks_per_search) || 0).toFixed(2)} clicks/search
                      </span>
                    </div>
                    <div style={{ fontSize: '0.8em', color: 'var(--text-light)' }}>
                      {adminMetrics.adminActivity.searches} Searches • {adminMetrics.adminActivity.clicks} Clicks
                      {adminMetrics.adminActivity.accounts > 1 && ` • ${adminMetrics.adminActivity.accounts} accounts`}
                    </div>
                  </li>
                )}
              </ul>
            </div>

            {/* Category Demand Trends */}
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <span className="dashboard-card-icon"><BrainCircuit size={24} /></span>
                <h3>Category Demand Trends</h3>
              </div>
              <ul className="dashboard-list">
                {adminMetrics.categoryTrends && adminMetrics.categoryTrends.map((s, i) => (
                  <li key={i} className="dashboard-list-item">
                    <span className="dashboard-item-title">{s.category}</span>
                    <span className="dashboard-stat-badge">{s.count} searches</span>
                  </li>
                ))}
                {(!adminMetrics.categoryTrends || adminMetrics.categoryTrends.length === 0) && <p style={{ color: 'var(--text-light)' }}>No categorized searches yet.</p>}
              </ul>
            </div>

            <div className="dashboard-card" style={{ gridColumn: '1 / -1' }}>
              <div className="dashboard-card-header">
                <span className="dashboard-card-icon"><TrendingUp size={24} /></span>
                <h3>Daily Search Volume (Last 7 Days)</h3>
              </div>
              <div style={{ padding: '20px 10px', height: '220px', position: 'relative' }}>
                {adminMetrics.dailyVolume && adminMetrics.dailyVolume.length > 0 ? (
                  <svg viewBox="0 0 1000 250" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                    <defs>
                      <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--primary-wine)" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="var(--primary-wine)" stopOpacity="0" />
                      </linearGradient>
                      <filter id="glow">
                        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                        <feMerge>
                          <feMergeNode in="coloredBlur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>

                    {/* Grid Lines */}
                    {[0, 25, 50, 75, 100].map(p => (
                      <line key={p} x1="0" y1={p * 2} x2="1000" y2={p * 2} stroke="rgba(0,0,0,0.05)" strokeWidth="1" />
                    ))}

                    {(() => {
                      const data = adminMetrics.dailyVolume;
                      const maxVal = Math.max(...data.map(d => Number(d.count)), 1);
                      const points = data.map((d, i) => {
                        const x = (i / (data.length - 1)) * 1000;
                        const y = 180 - (Number(d.count) / maxVal) * 160;
                        return { x, y, count: d.count, day: d.day };
                      });

                      const pathData = points.map(p => `${p.x},${p.y}`).join(' ');
                      const areaData = `0,200 ${pathData} 1000,200`;

                      return (
                        <>
                          {/* Gradient Area */}
                          <polyline points={areaData} fill="url(#chartGradient)" stroke="none" />

                          {/* Main Line */}
                          <polyline
                            points={pathData}
                            fill="none"
                            stroke="var(--primary-wine)"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            filter="url(#glow)"
                          />

                          {/* Data Points and Labels */}
                          {points.map((p, i) => (
                            <g key={i}>
                              <circle
                                cx={p.x}
                                cy={p.y}
                                r="5"
                                fill="white"
                                stroke="var(--primary-wine)"
                                strokeWidth="2"
                              />
                              <text
                                x={p.x}
                                y={p.y - 12}
                                textAnchor="middle"
                                fontSize="12"
                                fontWeight="bold"
                                fill="var(--primary-wine)"
                              >
                                {p.count}
                              </text>
                              <text
                                x={p.x}
                                y="200"
                                textAnchor="middle"
                                fontSize="10"
                                fill="var(--text-light)"
                              >
                                {p.day.split('-').slice(1).join('/')}
                              </text>
                            </g>
                          ))}
                        </>
                      );
                    })()}
                  </svg>
                ) : (
                  <p style={{ color: 'var(--text-light)', textAlign: 'center', marginTop: '50px' }}>No daily trend data available yet.</p>
                )}
              </div>
            </div>

            {/* Recent Search Trends */}
            <div className="dashboard-card" style={{ gridColumn: '1 / -1' }}>
              <div className="dashboard-card-header">
                <span className="dashboard-card-icon"><Search size={24} /></span>
                <h3>Recent Search Trends</h3>
              </div>
              <ul className="dashboard-list" style={{ maxHeight: '300px' }}>
                {adminMetrics.recentSearches?.map((s, i) => (
                  <li key={i} className="dashboard-list-item" style={{ alignItems: 'flex-start', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '8px' }}>
                      <span className="dashboard-item-title" style={{ fontWeight: 'bold' }}>{s.semantic_category || 'Uncategorized Search'}</span>
                      <span style={{ fontSize: '0.85em', color: 'var(--text-light)' }}>
                        {new Date(s.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <div className="dashboard-item-sub">
                      <span style={{ color: 'var(--primary-wine)', fontStyle: 'italic', marginRight: '8px' }}>"{s.query}"</span>
                      Searched by <strong>{s.name}</strong> from <em>{s.store_location}</em>
                    </div>
                  </li>
                ))}
                {adminMetrics.recentSearches.length === 0 && <p style={{ color: 'var(--text-light)' }}>No recent searches.</p>}
              </ul>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}

export default App;
